import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Folder as FolderIcon, FileText as FileTextIcon, Paperclip as PaperclipIcon, List as ListIcon, SlidersHorizontal as SlidersIcon } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useChat } from '@/contexts/ChatContext';
import { useAgentChat } from '@/hooks/useAgentChat';
import { useDrafting } from '@/hooks/useDrafting';
import { useSessionContext } from '@/hooks/useSessionContext';

import { SmartInput } from '@/components/SmartInput';
import { SplitPanelLayout } from '@/components/SplitPanelLayout';
import { ContractEditor } from '@/components/ContractEditor';
import { AnnotationPopover } from '@/components/annotations/AnnotationPopover';
import { AnnotationsPanel } from '@/components/annotations/AnnotationsPanel';
import { ReasoningPanel } from '@/components/ReasoningPanel';
import SequencerPanel from '@/components/draft/SequencerPanel';
import { FollowUpSuggestions } from '@/components/FollowUpSuggestions';
import { UsageWarningBanner } from '@/components/UsageWarningBanner';
import { DraftLoadingPlaceholder, DraftReadyToast } from '@/components/DraftLoadingPlaceholder';
import { DraftStageProgress } from '@/components/DraftStageProgress';
import { DraftReviewTabs } from '@/components/DraftReviewTabs';
import { DraftCompletionBubble } from '@/components/draft/DraftCompletionBubble';
import { ProgressEstimator } from '@/components/ProgressEstimator';
import { SourcesSideSheet } from '@/components/SourcesSideSheet';
import { PolicySideSheet } from '@/components/PolicySideSheet';
import { VaultPickerModal } from '@/components/composer/VaultPickerModal';
import { DocTypePopover } from '@/components/composer/DocTypePopover';
import { TemplatesPanel } from '@/components/composer/TemplatesPanel';
import { CustomizePopover } from '@/components/composer/CustomizePopover';
import { ContextChipBar } from '@/components/composer/ContextChipBar';
import { TERMS } from '@/lib/terminology';
import { getBackendBaseUrl } from '@/lib/backendUrl';
import { cn } from '@/lib/utils';
import { isDraftingIntent } from '@/lib/draftIntentDetector';
import { DashboardHomeHeader } from '@/components/dashboard/DashboardHomeHeader';
import { RecentMattersStrip } from '@/components/dashboard/RecentMattersStrip';
import { TodayPanel } from '@/components/dashboard/TodayPanel';
import OnboardingTour from '@/components/onboarding/OnboardingTour';
import { useTourState } from '@/components/onboarding/useTourState';

/**
 * Broadcast a template/prefill text to both the welcome textarea and the
 * active-chat SmartInput. Receivers listen for this event and set their
 * internal value.
 */
const dispatchComposerPrefill = (text) => {
  window.dispatchEvent(new CustomEvent('bayenah:composer-prefill', { detail: { text } }));
};

/**
 * AgentPreviewPage (`/v2`) — wires every Phase 1/2 piece together:
 *   • SmartInput (composer with quick-actions + file chips)
 *   • useAgentChat (SSE → /api/v2/agent)
 *   • ReasoningPanel (live agent thinking)
 *   • FollowUpSuggestions (after final_result)
 *   • SplitPanelLayout (chat ↔ editor with draggable divider)
 *   • ContractEditor (right pane — Markdown view/edit + downloads)
 *   • UsageWarningBanner (cost-cap heads-up)
 *
 * Harvey design tokens applied via `data-theme="harvey"` on the root.
 */
export default function AgentPreviewPage() {
  const { language } = useLanguage();
  const { session } = useAuth();
  const location = useLocation();
  // Cluster E.1.b (Feb 2026): the canonical matter id from the
  // dynamic route. Undefined when AgentPreviewPage is mounted at
  // `/chat` (no path param) — both the path-watcher and the
  // hash-watcher tie-breaker rely on this being truthy ⇔ deep-link.
  const { matter_id: routeMatterId } = useParams();
  const isArabic = language === 'ar';

  const {
    messages, reasoningSteps, isStreaming, currentDecision, error, sequencerState, sendMessage, stop, reset, loadEnvelope, sessionId,
  } = useAgentChat();
  const { callDraftAPI } = useDrafting();

  // Cluster B-3 (Feb 06 2026): sidebar-bridge. The live drafting path
  // writes the conversation envelope to `db.agent_sessions` but does
  // NOT touch `db.chats`, which is what the sidebar reads from. To
  // keep the sidebar in sync with agent sessions (and so the user
  // sees their fresh chat in "Recent" after a re-login → Step 7 of
  // the Cluster B session-lifecycle smoke), mirror each session
  // into `db.chats` via the existing `syncChatToCloud` helper.
  //
  // Triggered once when sessionId is first set AND there is at least
  // one assistant message — i.e., the user has a real conversation
  // to recall, not just an empty session id.
  const { syncChatToCloud, chats, setChats } = useChat();
  const sidebarBridgeRef = useRef(null);
  useEffect(() => {
    if (!sessionId) return;
    if (!messages || messages.length < 2) return;
    if (sidebarBridgeRef.current === sessionId) return;
    sidebarBridgeRef.current = sessionId;
    const userMsg = messages.find((m) => m.role === 'user');
    const firstAsst = messages.find((m) => m.role === 'assistant');
    if (!userMsg) return;
    const title = (userMsg.content || '').trim().slice(0, 80) || 'New chat';
    const nowIso = new Date().toISOString();
    const chat = {
      id: sessionId,
      title,
      messages: messages.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content || '',
        // Backend ChatMessageStore requires `timestamp`, not `createdAt`.
        // Iter71 follow-up: this mismatch was the source of the
        // recurring POST /api/auth/chats 422 on every chat hydration.
        timestamp: m.createdAt || nowIso,
      })),
      conversationId: sessionId,
      createdAt: userMsg.createdAt || nowIso,
      updatedAt: firstAsst?.createdAt || nowIso,
    };
    syncChatToCloud(chat);
    // Also mirror into local zustand so the sidebar updates without
    // waiting for the next /api/auth/chats refresh.
    if (!chats.some((c) => c.id === sessionId)) {
      setChats((prev) => [chat, ...prev]);
    }
  }, [sessionId, messages, chats, setChats, syncChatToCloud]);

  // Drafting-flow overlay (May-2026 routing fix). useAgentChat hits
  // /api/v2/agent (Conductor SSE). For explicit drafting intents we
  // bypass the Conductor and call /api/draft-jobs (async-job +
  // polling). The drafting flow has its own user/assistant bubbles
  // tracked here, rendered inline with the agent-chat messages.
  const [draftingMessages, setDraftingMessages] = useState([]);
  const [isDraftingFlow, setIsDraftingFlow] = useState(false);
  const [draftingError, setDraftingError] = useState(null);
  const draftAbortRef = useRef(null);

  // Unified message list — merges agent-chat messages (Conductor flow)
  // with drafting-flow overlay messages so the chat panel renders both
  // streams in a single chronological feed. Sorted by `createdAt` so
  // back-to-back drafting and Q&A exchanges interleave correctly.
  //
  // ⚠️  HOIST ORDER (production-down hotfix May-12 2026): this memo
  // MUST be declared before any useEffect that reads `allMessages`.
  // The previous placement after the useEffect block triggered a
  // temporal-dead-zone ReferenceError in the minified production
  // bundle ("Cannot access 're' before initialization").
  const allMessages = React.useMemo(() => {
    const merged = [...messages, ...draftingMessages];
    merged.sort((a, b) => {
      const ta = a.createdAt || a._ts || 0;
      const tb = b.createdAt || b._ts || 0;
      return ta - tb;
    });
    return merged;
  }, [messages, draftingMessages]);

  // Shared session context (vault docs, uploaded file, doc type, customise
  // settings) — drives both the WelcomeHero composer and the active-chat
  // SmartInput composer toolbar.
  const ctx = useSessionContext();

  // Composer modal/popover open state. DocType + Customize are anchored
  // popovers — the `*Anchor` keys say which surface (welcome | chat) the
  // popover is anchored to so we can position it relative to the right
  // button.
  const [vaultOpen, setVaultOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [docTypeAnchor, setDocTypeAnchor] = useState(null); // null | 'welcome' | 'chat'
  const [customizeAnchor, setCustomizeAnchor] = useState(null);
  const fileInputRef = useRef(null);

  const handleLocalFile = async (file) => {
    if (!file) return;
    // File-type allow-list: same as the /try trial path. Anything else
    // is rejected up-front before the backend even sees it.
    if (!/\.(pdf|docx?|txt)$/i.test(file.name)) {
      ctx.setUploadedFile({
        name: file.name,
        content: '',
        size: file.size,
        error: 'unsupported',
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      ctx.setUploadedFile({
        name: file.name,
        content: '',
        size: file.size,
        error: 'too_large',
      });
      return;
    }

    // .txt: read directly as UTF-8 (safe for plain text only).
    if (/\.txt$/i.test(file.name)) {
      const reader = new FileReader();
      reader.onload = () => {
        ctx.setUploadedFile({
          name: file.name,
          content: typeof reader.result === 'string' ? reader.result : '',
          size: file.size,
        });
      };
      reader.readAsText(file);
      return;
    }

    // PDF / DOCX: route through the backend extractor so we never
    // forward raw ZIP bytes as document_text. (Feb 2026 fix — same
    // bug as TryPage.jsx had earlier; the silent FileReader.readAsText
    // path was returning ZIP magic bytes for .docx, then /api/v2/agent's
    // binary-bytes guard correctly rejected it as HTTP 400 and the user
    // saw the failure inside the chat instead of a real review.)
    ctx.setUploadedFile({ name: file.name, content: '', size: file.size, extracting: true });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(
        `${getBackendBaseUrl()}/api/trial/extract-text`,
        { method: 'POST', body: fd },
      );
      if (!r.ok) {
        let detail = '';
        try { detail = (await r.json())?.detail || ''; } catch { /* noop */ }
        ctx.setUploadedFile({
          name: file.name,
          content: '',
          size: file.size,
          error: detail || 'extract_failed',
        });
        return;
      }
      const data = await r.json();
      ctx.setUploadedFile({
        name: file.name,
        content: data.text || '',
        size: file.size,
        extraction: data.extraction || null,
        contract_type: data.contract_type || null,
      });
    } catch (e) {
      ctx.setUploadedFile({
        name: file.name,
        content: '',
        size: file.size,
        error: 'network_error',
      });
    }
  };

  const composerActions = {
    onVault:     () => setVaultOpen(true),
    onTemplates: () => setTemplatesOpen(true),
    onFiles:     () => fileInputRef.current?.click(),
    onDocType:   (anchor) => setDocTypeAnchor((cur) => (cur === anchor ? null : anchor)),
    onCustomize: (anchor) => setCustomizeAnchor((cur) => (cur === anchor ? null : anchor)),
  };

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [editorContent, setEditorContent] = useState('');
  const [editorTitle, setEditorTitle] = useState('');
  // Cluster B-5: server-emitted clause map plumbed from the
  // assistant envelope's `output.clause_boundaries`.
  const [editorClauseBoundaries, setEditorClauseBoundaries] = useState(null);
  // Cluster B-1 (Feb 06 2026): stable id for the current draft so
  // AnnotationPopover and AnnotationsPanel can address the document
  // independently of its in-page text content.
  const [editorDraftId, setEditorDraftId] = useState(null);
  const [editorContractType, setEditorContractType] = useState(null);
  const [editorCitations, setEditorCitations] = useState([]);
  const [recentRefreshKey, setRecentRefreshKey] = useState(0);

  // Right-pane collapse state — controlled here so we can auto-expand
  // the moment a draft arrives.
  const [rightCollapsed, setRightCollapsed] = useState(true);
  // Timestamp the user submitted a draft request (for elapsed-time display).
  const [draftStartedAt, setDraftStartedAt] = useState(null);
  // Per-stage progress snapshot from the backend's `stage_progress` field
  // on the draft_jobs row. Drives the 5-step DraftStageProgress UI
  // (audit-2026-05-12 Bug #3 / Item 3).
  const [draftStageProgress, setDraftStageProgress] = useState(null);
  // Last drafting request (text + options) — captured at submission so
  // the error-bubble Retry CTA (Bug #1 fix) can replay it cleanly.
  const lastDraftRequestRef = useRef(null);
  // Brief "✓ تم!" toast when a draft completes.
  const [showReadyToast, setShowReadyToast] = useState(false);
  // Progress-estimator phase + start timestamp — surfaced inside the
  // streaming assistant bubble whenever a long pipeline is running.
  const [progressPhase, setProgressPhase] = useState(null);   // 'draft' | 'compliance' | …
  const [progressStartedAt, setProgressStartedAt] = useState(null);

  // Reset progress estimator when streaming ends.
  useEffect(() => {
    if (!isStreaming) {
      setProgressPhase(null);
      setProgressStartedAt(null);
    }
  }, [isStreaming]);

  // Sidebar "+ new chat" routes to /chat?new=<ts> — observe and reset.
  // Templates/Guidance pages route to /chat?prompt=<text> — auto-send.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('new')) {
      reset();
      setDraftingMessages([]);
      setDraftingError(null);
      setEditorContent('');
      setEditorTitle('');
      setEditorContractType(null);
      setEditorCitations([]);
      setRightCollapsed(true);
    }
    const prompt = params.get('prompt');
    // Cluster E.1.b (Feb 2026) race fix: when a canonical path
    // `/chat/:matter_id` is present, the path-rehydrate watcher
    // (below) is authoritative and will populate `allMessages`
    // asynchronously. The synchronous `allMessages.length === 0`
    // gate cannot see that future state, so we add an explicit
    // route-aware guard. Without this, navigating to
    // `/chat/<matter_id>?prompt=hi` would auto-send the prompt
    // BEFORE the rehydrate landed — polluting the rehydrated
    // matter with an unrelated user prompt.
    if (prompt && allMessages.length === 0 && !routeMatterId) {
      sendMessage(prompt);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, reset]);

  // Sidebar recent-list routes to /chat#session=<id> — rehydrate the
  // session via GET /api/v2/agent/sessions/{id}.
  //
  // Cluster E.1.b (Feb 2026) tie-breaker: when a canonical path param
  // `/chat/:matter_id` is ALSO present, the path watcher (below) is
  // authoritative — we skip the hash watcher entirely. Conservative
  // "new way wins over legacy way" rule per the locked decision.
  useEffect(() => {
    if (routeMatterId) return; // path watcher takes precedence
    const m = (location.hash || '').match(/^#session=(.+)$/);
    if (!m) return;
    const sid = decodeURIComponent(m[1]);
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = {};
        const token = session?.access_token;
        if (token) headers.Authorization = `Bearer ${token}`;
        const apiUrl = getBackendBaseUrl();
        const r = await fetch(`${apiUrl}/api/v2/agent/sessions/${encodeURIComponent(sid)}`, { headers });
        if (!r.ok) return;
        const env = await r.json();
        if (cancelled) return;
        loadEnvelope(env);
      } catch (e) {
        // silent — recent-list rehydrate is best-effort
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, routeMatterId]);

  // ────────────────────────────────────────────────────────────────
  // Cluster E.1.b (Feb 2026): canonical deep-link consumer.
  // /chat/:matter_id → rehydrate via GET /api/v2/agent/sessions/{id}.
  // Same fetch+loadEnvelope pattern as the hash watcher above; the
  // path watcher is the "new way" and wins on race (see tie-breaker
  // in the hash-watcher guard). 403 / 404 are silently ignored to
  // match the hash watcher's behaviour — the page falls through to
  // the default /chat view. No console noise on legitimate
  // auth-denial or missing-matter cases.
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!routeMatterId) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = {};
        const token = session?.access_token;
        if (token) headers.Authorization = `Bearer ${token}`;
        const apiUrl = getBackendBaseUrl();
        const r = await fetch(
          `${apiUrl}/api/v2/agent/sessions/${encodeURIComponent(routeMatterId)}`,
          { headers },
        );
        if (!r.ok) return; // 403/404 silently ignored
        const env = await r.json();
        if (cancelled) return;
        loadEnvelope(env);
      } catch (_e) {
        // network blip — leave page in default view
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeMatterId]);

  // Bump the refresh key whenever streaming finishes so the recent-runs
  // list picks up the just-completed session.
  useEffect(() => {
    if (!isStreaming && !isDraftingFlow && allMessages.length > 0) {
      setRecentRefreshKey((k) => k + 1);
    }
  }, [isStreaming, isDraftingFlow, allMessages.length]);

  // When the assistant's final_result lands and includes draft/review text,
  // surface it in the right-pane editor.
  useEffect(() => {
    const lastAssistant = [...allMessages].reverse().find((m) => m.role === 'assistant' && !m.isStreaming);
    if (!lastAssistant) return;
    const out = lastAssistant.meta?.output || {};
    const text = out.draft_text || out.review_text || '';
    if (text && text !== editorContent) {
      const isSubstantial = text.trim().length > 50;
      const wasEmpty = !editorContent;
      const loadingDone = !isStreaming && !isDraftingFlow;

      setEditorContent(text);
      setEditorTitle(out.contract_type
        ? (isArabic ? `مسودة: ${out.contract_type}` : `Draft: ${out.contract_type}`)
        : (isArabic ? 'مستند الوكيل' : 'Agent Document'));
      setEditorContractType(out.contract_type || null);
      // Cluster B-5: pull the server-emitted clause map onto the
      // editor surface so view-mode wraps each clause in a
      // `[data-clause-id]` span that Phase-2 popovers anchor to.
      setEditorClauseBoundaries(Array.isArray(out.clause_boundaries) ? out.clause_boundaries : null);
      // Cluster B-1: stable draft id for annotations.
      const nextDraftId = out.draft_id || null;
      const prevDraftId = editorDraftId;
      setEditorDraftId(nextDraftId);
      // Cluster B.1 (Feb 06 2026): when the editor transitions to a
      // NEW draft_id (i.e., a Redraft just completed), notify the
      // annotation surfaces that the carry-forward step may have
      // inserted re-anchored rows on the new document. QueueSection /
      // AnnotationsPanel both listen for `bayenah:annotation-created`
      // and re-fetch the user's queue when it fires — this same event
      // is the simplest cross-cutting refresh signal we already have.
      if (
        prevDraftId
        && nextDraftId
        && prevDraftId !== nextDraftId
        && typeof window !== 'undefined'
      ) {
        window.dispatchEvent(new CustomEvent('bayenah:annotation-created', {
          detail: { source: 'carry-forward', from_doc: prevDraftId, to_doc: nextDraftId },
        }));
      }
      setEditorCitations((lastAssistant.meta?.sources || []).slice(0, 8).map((s, idx) => ({
        id: idx,
        source: `${s.law_name || ''} ${s.article_label || ''}`.trim() || 'Source',
        text: (s.snippet || '').slice(0, 120),
        url: s.url || null,
      })));
      // Auto-expand right pane.
      setRightCollapsed(false);
      setDraftStartedAt(null);
      // "Done! Draft ready" toast — fires ONLY when (a) we just
      // transitioned from empty editor to populated, (b) the agent
      // pipeline is actually done streaming, and (c) the content is
      // substantial (not a stray empty draft_text="" payload). Without
      // these guards the banner can flash before the editor mounts.
      // Jul 2026: never while a sequencer run is live — with the panel
      // now open from draft start, this used to paint "Done!" over a
      // draft that was still being written.
      if (isSubstantial && wasEmpty && loadingDone && !sequencerState?.active) {
        setShowReadyToast(true);
        const t = setTimeout(() => setShowReadyToast(false), 2500);
        return () => clearTimeout(t);
      }
    }
  }, [allMessages, editorContent, editorDraftId, isArabic, isStreaming, isDraftingFlow]);

  // Whenever the user submits a message and we're streaming again, mark
  // a fresh draft start time. Note: we no longer auto-expand the right
  // pane on plain streaming — the pane is reserved for actual drafts /
  // reviews / compliance output. The drafting flow (`runDraftingFlow`)
  // expands the pane itself when the user explicitly requests a draft,
  // and the final-result effect expands it when draft/review text
  // arrives. Q&A questions leave the pane collapsed.
  useEffect(() => {
    if ((isStreaming || isDraftingFlow) && draftStartedAt === null) {
      setDraftStartedAt(Date.now());
      // Only auto-expand for explicit draft flows; Q&A should leave
      // the pane collapsed until/unless content materialises.
      if (isDraftingFlow) {
        setRightCollapsed(false);
      }
    }
  }, [isStreaming, isDraftingFlow, draftStartedAt]);

  // Jul 2026 (live-typing flow): open the document panel the moment a
  // draft run STARTS (Scribe agent present in the sequencer), not when
  // the first text lands — with parallel section streaming the panel
  // fills within seconds, and an already-open panel is where the user
  // watches the agreement being written.
  const scribeRunActive = Boolean(sequencerState?.active && sequencerState?.agents?.scribe);
  useEffect(() => {
    if (scribeRunActive) setRightCollapsed(false);
  }, [scribeRunActive]);

  // When a Q&A response lands (final_result with no draft_text / review_text)
  // and the right pane has no active draft content, make sure the pane
  // is collapsed. Prevents the pane from being stuck open after a draft
  // → Q&A handoff in the same chat.
  useEffect(() => {
    if (isStreaming || isDraftingFlow) return;
    const lastAssistant = [...allMessages].reverse().find((m) => m.role === 'assistant' && !m.isStreaming);
    if (!lastAssistant) return;
    const out = lastAssistant.meta?.output || {};
    const hasDraftOrReview = Boolean(out.draft_text || out.review_text);
    if (!hasDraftOrReview && !editorContent && !sequencerState?.scribe_draft_text) {
      setRightCollapsed(true);
    }
  }, [allMessages, isStreaming, isDraftingFlow, editorContent, sequencerState?.scribe_draft_text]);

  // Listen for follow-up clicks from Bubble's window event
  useEffect(() => {
    const handler = (e) => {
      const suggestion = e.detail?.suggestion;
      if (suggestion) sendMessage(suggestion);
    };
    window.addEventListener('bayenah:follow-up-selected', handler);
    return () => window.removeEventListener('bayenah:follow-up-selected', handler);
  }, [sendMessage]);

  /**
   * Drafting-flow runner. Bypasses useAgentChat and routes directly
   * to /api/draft-jobs. Manages its own user/assistant bubbles via
   * setDraftingMessages so the chat panel keeps a unified feed.
   */
  const runDraftingFlow = useCallback(async (text, options) => {
    // eslint-disable-next-line no-console
    console.log('[DRAFTING_FLOW]', 'runDraftingFlow:enter', {
      text_length: (text || '').length,
      has_document_text: Boolean(options?.documentText),
      contract_type: options?.contractType || null,
    });
    // Snapshot request for the Retry CTA (audit-2026-05-12 Bug #1).
    lastDraftRequestRef.current = { text, options: options || {} };
    setDraftStageProgress(null);
    const userMsgId = `u-draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const assistantMsgId = `a-draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const now = Date.now();
    const userMsg = {
      id: userMsgId,
      role: 'user',
      content: text,
      createdAt: now,
      _ts: now,
      meta: {
        documentText: options?.documentText || undefined,
        contractType: options?.contractType || undefined,
      },
    };
    const placeholderAssistantMsg = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      isStreaming: true,
      createdAt: now + 1,
      _ts: now + 1,
      meta: { drafting: true },
    };
    setDraftingMessages((prev) => [...prev, userMsg, placeholderAssistantMsg]);
    setIsDraftingFlow(true);
    setDraftingError(null);
    setDraftStartedAt(now);
    setRightCollapsed(false);
    // eslint-disable-next-line no-console
    console.log('[DRAFTING_FLOW]', 'runDraftingFlow:placeholder_mounted', {
      user_msg_id: userMsgId, assistant_msg_id: assistantMsgId,
    });

    // Build a synthetic message list — useDrafting reads the latest user
    // message from this array and posts it as the prompt.
    const draftingMsgList = [{ role: 'user', content: text }];

    const controller = new AbortController();
    draftAbortRef.current = controller;

    try {
      // eslint-disable-next-line no-console
      console.log('[DRAFTING_FLOW]', 'runDraftingFlow:calling_callDraftAPI', {
        doc_type: options?.contractType || options?.docType || null,
      });
      const result = await callDraftAPI(
        draftingMsgList,
        null, // conversation_id — let backend mint one
        controller.signal,
        {
          docType: options?.contractType || options?.docType,
          documentText: options?.documentText,
          onStage: (_stageString, payload) => {
            // Forward both the legacy stage label and the structured
            // stage_progress block. The progress UI prefers the
            // structured payload; the legacy `stage` is kept around
            // for compatibility with older code paths.
            if (payload?.stage_progress) {
              setDraftStageProgress(payload.stage_progress);
            }
          },
        },
      );
      // eslint-disable-next-line no-console
      console.log('[DRAFTING_FLOW]', 'runDraftingFlow:result_ok', {
        word_count: result?.meta?.word_count,
        draft_id: result?.meta?.draft_id,
      });

      // Patch the placeholder with the completed result. The
      // editor-sync useEffect watches messages.meta.output.draft_text
      // — match that shape so the right-pane auto-fills the editor.
      setDraftingMessages((prev) => prev.map((m) => (
        m.id === assistantMsgId
          ? {
              ...m,
              isStreaming: false,
              content: result.text || '',
              meta: {
                drafting: true,
                output: {
                  draft_text: result.text || '',
                  contract_type: result?.meta?.contract_type || null,
                },
                sources: [],
                draft_id: result?.meta?.draft_id || null,
              },
            }
          : m
      )));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('[DRAFTING_FLOW]', 'runDraftingFlow:error', {
        error_name: err?.name,
        error_message: err?.message,
        error_code: err?.error_code,
        response_status: err?.response?.status,
        response_detail: err?.response?.data?.detail,
      });
      if (err?.name === 'CanceledError' || err?.message === 'Aborted' || controller.signal.aborted) {
        setDraftingMessages((prev) => prev.filter((m) => m.id !== assistantMsgId));
      } else {
        // Bilingual user-facing copy lives HERE (frontend i18n), NOT on
        // the backend. Backend `error_code` is machine-readable only.
        // (audit-2026-05-12 Bug #1 — Modification 1 in approved plan.)
        const userMessage = isArabic
          ? 'تعذّر إنشاء المسودة. حاول مرة أخرى.'
          : 'Draft generation failed. Please retry.';
        setDraftingError(userMessage);
        setDraftingMessages((prev) => prev.map((m) => (
          m.id === assistantMsgId
            ? {
                ...m,
                isStreaming: false,
                content: userMessage,
                meta: {
                  drafting: true,
                  error: true,
                  error_code: err?.error_code || null,
                  canRetry: true,
                },
              }
            : m
        )));
      }
    } finally {
      setIsDraftingFlow(false);
      draftAbortRef.current = null;
      // eslint-disable-next-line no-console
      console.log('[DRAFTING_FLOW]', 'runDraftingFlow:exit', {});
    }
  }, [callDraftAPI, isArabic]);

  // Retry handler for the error bubble's CTA. Re-fires the last
  // drafting request with the exact same text + options that produced
  // the failure (audit-2026-05-12 Bug #1).
  const retryLastDraft = useCallback(() => {
    const snapshot = lastDraftRequestRef.current;
    if (!snapshot) return;
    setDraftingError(null);
    runDraftingFlow(snapshot.text, snapshot.options);
  }, [runDraftingFlow]);

  const handleSubmit = async (text, files, action) => {
    // Intent inference — when the caller does not tag the submission
    // with an explicit action, look at the message text. If the user
    // clearly asks for a contract review or compliance audit, route
    // through the Sequencer fan-out (`full_review`) instead of the
    // legacy single-agent path. Without this, fresh-chat
    // "review this contract" from WelcomeHero sends override_task=null
    // and the SequencerPanel UI never mounts.
    //
    // Only triggers for sufficiently specific cues so a casual mention
    // of the word "review" in an unrelated question doesn't pre-empt
    // the backend router.
    let resolvedAction = action;

    // ── May-2026 ROUTING FIX ── Strict explicit-verb drafting detection
    // takes precedence over the review heuristic below. If the user
    // Sprint 4 (May 2026): chat-initiated drafts now route to the
    // multi-agent pipeline via /api/v2/agent with override_task='full_draft'.
    // Scribe writes the document → Examiner + Regis review in parallel →
    // Conductor synthesises findings. The legacy `useDrafting` →
    // /api/draft-jobs path is retired for chat drafts.
    const draftIntentDetected = (
      resolvedAction === 'draft'
      || (typeof text === 'string' && (isFullDraftIntent(text) || isDraftingIntent(text, language)))
    );
    if (draftIntentDetected) {
      // Force resolvedAction so mapActionToTask returns 'full_draft'.
      resolvedAction = 'draft';
      // eslint-disable-next-line no-console
      console.log('[FRONTEND_ROUTE_DECISION] target=v2_agent_full_draft reason=' + (
        resolvedAction === 'draft' ? 'quick_action_draft' : 'detected_intent'
      ));
    }

    if (!resolvedAction && typeof text === 'string') {
      const lower = text.toLowerCase();
      // EN cues: "review …" / "audit …" / "check this contract" /
      // "analyse this agreement" / "look at this contract"
      // AR cues: "راجع", "مراجعة", "افحص", "حلّل", "تحليل"
      const reviewIntent = (
        /\b(review|audit|analyse|analyze|check)\b.*\b(contract|agreement|nda|sla|document)\b/i.test(text)
        || /\b(review|audit)\s+(this|my)\b/i.test(text)
        || /^\s*(please\s+)?(review|audit)\b/i.test(text)
        || /راجع|مراجعة|افحص|حلّل|حلل|تحليل/.test(text)
      );
      const ctxPayload = ctx.buildPayload();
      const hasDoc = (
        Boolean((ctxPayload.documentText || '').trim())
        || (files && files.length > 0)
      );
      if (reviewIntent || (hasDoc && lower.length >= 5)) {
        resolvedAction = 'review';
      }
    }

    const overrideTask = mapActionToTask(resolvedAction);

    // eslint-disable-next-line no-console
    console.log('[FRONTEND_ROUTE_DECISION] target=conductor reason=default override_task=' + (overrideTask || 'none') + ' TODO: remove after 7 days');

    // Quick-action submissions arrive with an empty textarea — `useAgentChat`
    // bails out on `!message.trim()`, so we synthesise a user-visible
    // prompt that matches the action.  Without this, clicking the
    // Compliance quick-action with a doc loaded would silently no-op.
    let resolvedText = (text || '').trim();
    if (!resolvedText && resolvedAction) {
      const isArabicNow = language === 'ar';
      const PROMPT_FOR_ACTION = {
        review:     isArabicNow ? 'راجع هذه المسودة بشكل شامل: مخاطر البنود وفجوات الامتثال والسياق التنظيمي.'
                                : 'Run a full review of this draft: clause risks, compliance gaps, and regulatory context.',
        compliance: isArabicNow ? 'افحص هذه المسودة مقابل الأنظمة السعودية ذات الصلة وحدد فجوات الامتثال مع الإسناد للمواد النظامية.'
                                : 'Audit this draft against the relevant Saudi laws and identify compliance gaps with article citations.',
        improve:    isArabicNow ? 'راجع هذه المسودة وحسّن صياغتها القانونية واقترح تحسينات موثقة.'
                                : 'Review this draft and improve the legal wording, suggest documented enhancements.',
      };
      resolvedText = PROMPT_FOR_ACTION[resolvedAction] || resolvedAction;
    }

    // Tag the progress phase so the in-flight bubble shows the right copy.
    // `full_review` (Sequencer fan-out) reuses the review-phase copy.
    setProgressPhase(
      (overrideTask === 'review' || overrideTask === 'full_review') ? 'review'
      : overrideTask === 'gap_analysis' ? 'compliance'
      : 'draft',
    );
    setProgressStartedAt(Date.now());

    // ── Jul 2026 FIX (empty-review bug) ──────────────────────────────
    // Files attached via the composer chip bar (SmartInput) were
    // collected into `attachedFiles` but NEVER extracted or sent — the
    // review/draft fired with no document, the backend returned an
    // empty envelope, and the user saw "{}" with a phantom "review in
    // the document panel". Extract chip-bar attachments through the
    // same backend extractor the Files&Sources path uses, and merge
    // the text into the payload before sending.
    let attachedDocText = '';
    if (files?.length) {
      const file = files[0];
      try {
        if (/\.txt$/i.test(file.name)) {
          attachedDocText = await file.text();
        } else {
          const fd = new FormData();
          fd.append('file', file);
          const r = await fetch(
            `${getBackendBaseUrl()}/api/trial/extract-text`,
            { method: 'POST', body: fd },
          );
          if (r.ok) {
            const data = await r.json();
            attachedDocText = data.text || '';
          }
        }
      } catch {
        // Extraction failed — the backend's empty-document guard will
        // surface an explicit error instead of a silent empty review.
      }
    }

    // Merge the shared session context (vault docs, uploaded file, picked
    // document type, customise knobs) into the agent payload.
    const payload = ctx.buildPayload();
    sendMessage(resolvedText, {
      overrideTask,
      documentText: payload.documentText || attachedDocText || undefined,
      contractType: payload.contractType || undefined,
      extra: Object.keys(payload.extra).length ? payload.extra : undefined,
    });

    if (files?.length) setAttachedFiles([]); // clear after submit
    ctx.clearAll();
  };

  // Retry compliance against the original document of a prior assistant
  // message — used by the "Run Compliance Check" CTA inside a
  // failed-Regis bubble.  Walks back from `assistantMsgId` to find the
  // parent user message and reuses its persisted documentText.
  const handleRetryCompliance = useCallback((assistantMsgId) => {
    const idx = messages.findIndex((m) => m.id === assistantMsgId);
    if (idx <= 0) return;
    // Find the immediately-preceding user message.
    let parent = null;
    for (let i = idx - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        parent = messages[i];
        break;
      }
    }
    const docText = parent?.meta?.documentText;
    if (!docText) return;
    setProgressPhase('compliance');
    setProgressStartedAt(Date.now());
    const isArabicNow = language === 'ar';
    const retryPrompt = isArabicNow
      ? 'افحص هذه المسودة مقابل الأنظمة السعودية ذات الصلة وحدد فجوات الامتثال مع الإسناد للمواد النظامية.'
      : 'Audit this draft against the relevant Saudi laws and identify compliance gaps with article citations.';
    sendMessage(retryPrompt, {
      overrideTask: 'gap_analysis',
      documentText: docText,
      contractType: parent?.meta?.contractType || undefined,
      extra: parent?.meta?.extra || undefined,
    });
  }, [messages, sendMessage, language, setProgressPhase, setProgressStartedAt]);

  // Cluster B-9 (iter75 follow-up, Feb 06 2026): persist manual edits
  // from ContractEditor's edit mode back to agent_sessions so that a
  // reload restores the user's tweaks. PATCH /api/v2/agent/sessions/{id}
  // accepts an optional `draft_text` field (iter75 backend extension)
  // and recomputes `clause_boundaries` server-side so Phase-2
  // annotations stay anchored after a manual edit.
  const handleEditorSave = useCallback(async (nextText) => {
    if (!sessionId) return;
    if (typeof nextText !== 'string') return;
    try {
      const apiUrl = getBackendBaseUrl();
      const token = session?.access_token;
      await fetch(
        `${apiUrl}/api/v2/agent/sessions/${encodeURIComponent(sessionId)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ draft_text: nextText }),
        },
      );
    } catch {
      /* best-effort — local state is the source of truth until reload */
    }
  }, [sessionId, session?.access_token]);

  // Wire ContractEditor's bottom action bar to the chat backend, using
  // the current draft as context for the next request. The draft text is
  // sent via `documentText` (request body) rather than inlined into the
  // visible user message — keeps the chat panel readable.
  const handleEditorAction = (id) => {
    if (!editorContent) return;
    const ctxType = editorTitle?.replace(/^مسودة:\s*|^Draft:\s*/, '').trim() || null;

    // Map: button id → { display, prompt, overrideTask }
    const cfg = {
      review: {
        display: isArabic ? 'جاري المراجعة الشاملة (فاحص + ريجس + مستشار)...' : 'Running full review (Examiner + Regis + Counsel)...',
        prompt:  isArabic
          ? 'راجع هذه المسودة بشكل شامل: مخاطر البنود، فجوات الامتثال، والسياق التنظيمي.'
          : 'Run a full review: clause risks, compliance gaps, and regulatory context.',
        // Sprint 2 — review now triggers the Sequencer fan-out
        // (Examiner + Regis + Counsel + Conductor) instead of the
        // single-agent path. The orchestrator branches on
        // override_task='full_review'.
        overrideTask: 'full_review',
      },
      redraft: {
        display: isArabic ? 'جاري إعادة صياغة المسودة...' : 'Redrafting the document...',
        prompt:  isArabic
          ? 'أعد صياغة هذه المسودة بنفس الإطار القانوني مع تحسين الصياغة وتعزيز الحماية النظامية. أبقِ على نفس الأطراف والنوع.'
          : 'Redraft this document in the same legal framework with tighter wording and stronger statutory protection. Keep the same parties and type.',
        // Cluster B.1 (iter80, Feb 06 2026): route Redraft through the
        // multi-agent `full_draft` pipeline (Scribe + Examiner + Regis
        // + Conductor) instead of the single-agent `draft` task.
        // The single-agent branch's classifier was returning
        // 'unclear' in EN even with a non-empty document_text and a
        // well-formed redraft directive, so no v2 envelope ever got
        // persisted. full_draft honors document_text deterministically
        // and is what the user actually wants on a Redraft anyway.
        overrideTask: 'full_draft',
      },
      compliance: {
        display: isArabic ? 'جاري فحص الامتثال النظامي للاتفاقية...' : 'Running compliance check on this agreement...',
        prompt:  isArabic
          ? 'افحص هذه المسودة مقابل الأنظمة السعودية ذات الصلة وحدد فجوات الامتثال مع الإسناد للمواد النظامية.'
          : 'Audit this draft against the relevant Saudi laws and identify compliance gaps with article citations.',
        overrideTask: 'gap_analysis',
      },
      improve: {
        display: isArabic ? 'جاري تحسين المسودة...' : 'Improving the draft...',
        prompt:  isArabic
          ? 'راجع هذه المسودة وحسّن الصياغة القانونية، أضف أي حمايات ناقصة، واقترح تحسينات موثقة.'
          : 'Review this draft and improve the legal wording, add any missing protections, and suggest documented enhancements.',
        overrideTask: 'review',
      },
    };

    const c = cfg[id];
    if (!c) return;
    setProgressPhase(id === 'redraft' ? 'redraft' : (id === 'compliance' ? 'compliance' : id === 'improve' ? 'improve' : 'review'));
    setProgressStartedAt(Date.now());
    // Cluster B.1 (Feb 06 2026): send the actual `c.prompt` as the
    // message so the orchestrator classifier sees a real redraft
    // directive in both EN and AR. Previously we sent the UI
    // placeholder `c.display` ("Redrafting the document...") which
    // classified as 'unclear' in EN — orchestrator returned a
    // clarification asking for source doc context, no v2 draft_id
    // was produced, carry-forward never fired. AR happened to pass
    // because its display string contained "أعد صياغة" — but the
    // semantic was wrong either way. Pass the prompt as the message;
    // preserve the user-visible placeholder via the optimistic FE
    // message in useAgentChat's userPreview slot.
    sendMessage(c.prompt, {
      overrideTask: c.overrideTask,
      documentText: editorContent,
      contractType: ctxType,
      userPreview: c.display,
      extra: { user_intent: c.prompt },
    });
  };

  const isEmpty = allMessages.length === 0;

  // Cluster J.C (Feb 2026) — onboarding tour. Mounts ONLY on the
  // /chat empty state (no active matter, no messages). If the user
  // deep-links straight to /chat/:matter_id we defer to their next
  // /chat visit per spec (decision: tour only on empty state).
  //
  // Mount delay: the `RecentMattersStrip` component fetches its
  // matters list via axios — the testid is briefly absent during the
  // initial paint. We delay flipping `run=true` by 700 ms so the
  // step #4 `targetExists()` filter inside OnboardingTour sees the
  // strip in DOM and includes the stop. 700 ms is conservative for
  // the local Mongo round-trip; preview is typically < 300 ms.
  const tour = useTourState();
  const [tourMountReady, setTourMountReady] = React.useState(false);
  React.useEffect(() => {
    if (!tour.shouldRun || !isEmpty || sessionId) {
      setTourMountReady(false);
      return undefined;
    }
    const id = window.setTimeout(() => setTourMountReady(true), 700);
    return () => window.clearTimeout(id);
  }, [tour.shouldRun, isEmpty, sessionId]);
  const tourCanRun = tour.shouldRun && isEmpty && !sessionId && tourMountReady;
  const draftLoaded = Boolean(editorContent);

  const left = (
    <div className="flex flex-col h-full" style={{ background: 'var(--canvas)' }}>
      <UsageWarningBanner />

      {isEmpty ? (
        <>
          <WelcomeHero
            isArabic={isArabic}
            ctx={ctx}
            composerActions={composerActions}
            docTypeAnchor={docTypeAnchor}
            customizeAnchor={customizeAnchor}
            closeDocType={() => setDocTypeAnchor(null)}
            closeCustomize={() => setCustomizeAnchor(null)}
            onSubmit={(text) => handleSubmit(text, [], null)}
          />
          {/* Cluster J.C — onboarding tour, mounted alongside the
              empty-state hero so all six target testids are guaranteed
              to be in the DOM at run-time. */}
          <OnboardingTour
            run={tourCanRun}
            onFinish={tour.markComplete}
          />
        </>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto space-y-4 px-4 py-4" data-testid="agent-preview-messages">
            {allMessages.map((m) => (
              <React.Fragment key={m.id}>
                {/* Jul 2026 (one-progress-surface rule): while the
                    sequencer strip is active it is the ONLY progress
                    artifact. The empty streaming assistant bubble
                    ("..." + phase estimator) is suppressed — it was
                    one of four stacked progress surfaces confusing
                    users during drafting. */}
                {!(sequencerState?.active && m.role === 'assistant' && m.isStreaming && !(m.content || '').trim()) && (
                <Bubble
                  message={m}
                  isArabic={isArabic}
                  progressPhase={progressPhase}
                  progressStartedAt={progressStartedAt}
                  onRetryCompliance={handleRetryCompliance}
                  sequencerActive={Boolean(sequencerState?.active)}
                />
                )}
                {m.role === 'assistant' && m.meta?.drafting && m.meta?.error && m.meta?.canRetry && (
                  <div
                    className="flex"
                    style={{ justifyContent: isArabic ? 'flex-end' : 'flex-start' }}
                    data-testid={`drafting-retry-row-${m.id}`}
                  >
                    <button
                      type="button"
                      onClick={retryLastDraft}
                      data-testid="drafting-retry-button"
                      style={{
                        background: '#111', color: '#fff',
                        padding: '8px 16px', borderRadius: 9999,
                        fontSize: 13, fontWeight: 500, border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {isArabic ? 'إعادة المحاولة' : 'Retry'}
                    </button>
                  </div>
                )}
              </React.Fragment>
            ))}
            {(isStreaming || sequencerState?.completed) && sequencerState?.active && (
              <SequencerPanel state={sequencerState} isArabic={language === 'ar'} />
            )}
            {/* Sep 2026 (single-progress-list) — the reasoning list is
                rendered inside the streaming assistant bubble
                (auto-expanded during streaming, collapsible after).
                The prior top-level ReasoningPanel duplicated it. */}
            {(error || draftingError) && (
              <div className="text-sm text-red-600" data-testid="agent-preview-error">{error || draftingError}</div>
            )}
          </div>

          <div className="px-4 pb-4">
            <ContextChipBar ctx={ctx} isArabic={isArabic} />
            <div className="relative">
              <ChatComposerToolbar
                isArabic={isArabic}
                ctx={ctx}
                composerActions={composerActions}
                anchor="chat"
                docTypeAnchor={docTypeAnchor}
                customizeAnchor={customizeAnchor}
                closeDocType={() => setDocTypeAnchor(null)}
                closeCustomize={() => setCustomizeAnchor(null)}
              />
            </div>
            <SmartInput
              onSubmit={handleSubmit}
              attachedFiles={attachedFiles}
              onAttachFiles={(files) => setAttachedFiles((prev) => [...prev, ...files])}
              onRemoveFile={(file) => setAttachedFiles((prev) => prev.filter((f) => f !== file))}
              isSubmitting={isStreaming}
              placeholder={
                draftLoaded
                  ? (isArabic
                    ? 'اسأل عن هذه الاتفاقية، اطلب تعديلاً في بند، أو أضف شرطاً جديداً...'
                    : 'Ask about this agreement, request a clause change, or add a new provision...')
                  : (isArabic
                    ? 'اسأل سؤالاً قانونياً، أو ارفع عقداً للمراجعة، أو اطلب صياغة مستند...'
                    : 'Ask a legal question, upload a contract for review, or request a draft...')
              }
              showImproveButton={allMessages.some((m) => m.role === 'assistant' && !m.isStreaming)}
              onImprove={() => {
                const last = [...allMessages].reverse().find((m) => m.role === 'assistant');
                if (last?.content) {
                  sendMessage(isArabic ? `حسّن الإجابة السابقة وأضف تفاصيل أكثر.` : 'Improve the previous answer with more detail.');
                }
              }}
            />
            {(isStreaming || isDraftingFlow) && (
              <button
                onClick={() => {
                  if (isStreaming) stop();
                  if (isDraftingFlow && draftAbortRef.current) draftAbortRef.current.abort();
                }}
                className="mt-2 text-xs hv-text-muted hover:hv-text"
                data-testid="agent-preview-stop"
              >
                {isArabic ? 'إيقاف' : 'Stop generating'}
              </button>
            )}
            {!isStreaming && !isDraftingFlow && (
              <button
                onClick={() => {
                  reset();
                  setDraftingMessages([]);
                  setDraftingError(null);
                }}
                className="mt-2 ms-3 text-xs hv-text-muted hover:hv-text"
                data-testid="agent-preview-reset"
              >
                {isArabic ? 'محادثة جديدة' : 'New session'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Determine if a draft is in progress. Only the explicit drafting
  // flow (`runDraftingFlow` → /api/draft-jobs) triggers the right-pane
  // loading visualisation. The general /api/v2/agent stream may also
  // be a Q&A question that should leave the pane untouched.
  const draftInProgress = isDraftingFlow && !editorContent;
  // Pull the most-recent reasoning step text for the loading placeholder.
  const lastStepText = (() => {
    if (!reasoningSteps || reasoningSteps.length === 0) return '';
    const last = reasoningSteps[reasoningSteps.length - 1];
    return last?.content || last?.step || '';
  })();

  // Sprint 4: detect whether the live/just-completed flow is a
  // full_draft run. Signal is the presence of scribe_draft_text on
  // sequencerState (populated by `scribe_token` deltas during
  // streaming and by `scribe_complete`/poll-fallback as the source
  // of truth at agent finish).
  // Jul 2026: live parallel section texts, assembled in section order.
  // While the 5 concurrent Scribe calls stream, this is the freshest
  // view of the draft; `scribe_draft_text` (whole completed sections)
  // remains the authority once it grows past the assembly.
  const liveAssembledDraft = (() => {
    const texts = sequencerState?.section_texts;
    if (!texts) return '';
    const order = sequencerState?.draft_sections_order || Object.keys(texts);
    return order.map((k) => texts[k]).filter(Boolean).join('\n\n');
  })();
  const liveDraftText = (
    (sequencerState?.scribe_draft_text || '').length >= liveAssembledDraft.length
      ? (sequencerState?.scribe_draft_text || '')
      : liveAssembledDraft
  );
  const fullDraftMode = Boolean(liveDraftText)
    || Boolean(sequencerState?.active && sequencerState?.agents?.scribe);
  const conductorCompleted = sequencerState?.conductor?.status === 'completed';
  const fullDraftReview = (() => {
    if (!fullDraftMode) return '';
    // The Conductor's findings text is delivered as the final
    // assistant message. We only surface it once `conductor_complete`
    // has fired, so Fix-3's pulse animation lines up with content
    // arrival. Before that, the Review tab shows the "No review
    // available yet" placeholder.
    if (!conductorCompleted) return '';
    const last = [...messages].reverse().find((m) => m.role === 'assistant' && (m.content || '').length > 0);
    return last?.content || '';
  })();

  const right = (fullDraftMode)
    ? (
      <DraftReviewTabs
        draftText={liveDraftText}
        reviewText={fullDraftReview}
        isArabic={isArabic}
        contractType={editorContractType}
        citations={editorCitations}
        onAction={handleEditorAction}
        conductorCompleted={conductorCompleted}
        // P2 fix (Feb 2026): we're in `fullDraftMode` which only fires
        // after Scribe (= Bayenah) generated this draft. Hide the
        // Review tab — Bayenah reviewing its own output produced
        // phantom findings on a live 26-article NDA.
        sourceKind="bayenah_drafted"
      />
    )
    : editorContent
    ? (
      <ContractEditor
        content={editorContent}
        onChange={setEditorContent}
        title={editorTitle}
        contractType={editorContractType}
        citations={editorCitations}
        clauseBoundaries={editorClauseBoundaries}
        mode="view"
        showActions={true}
        onAction={handleEditorAction}
        onSave={handleEditorSave}
        matterId={sessionId}
      />
    )
    : draftInProgress
      ? (
        // Show the new 5-step progress visualisation as soon as the
        // backend publishes its first stage_progress snapshot. Until
        // then (~first 3s) fall back to the legacy spinner so the
        // pane is never empty (audit-2026-05-12 Bug #3 / Item 3).
        draftStageProgress
          ? (
            <DraftStageProgress
              progress={draftStageProgress}
              startedAt={draftStartedAt}
              isArabic={isArabic}
              isComplete={false}
              isFailed={false}
            />
          )
          : (
            <DraftLoadingPlaceholder
              isArabic={isArabic}
              startedAt={draftStartedAt}
              lastStep={lastStepText}
            />
          )
      )
      : (
        <div className="h-full flex items-center justify-center text-center px-6 hv-surface">
          <div className="hv-text-muted text-sm max-w-sm">
            {isArabic
              ? 'سيظهر هنا المستند بمجرد توليد مسودة أو تقرير من الوكيل.'
              : 'Generated drafts and review reports will appear here once the agent finishes.'}
          </div>
        </div>
      );

  // Wrap right with the success toast.
  const rightWithToast = (
    <div className="h-full flex flex-col">
      <DraftReadyToast
        isArabic={isArabic}
        visible={showReadyToast && !(sequencerState?.active && !conductorCompleted)}
      />
      <div className="flex-1 overflow-auto">{right}</div>
    </div>
  );

  // Cluster B / Q1-c (Feb 06 2026): partial deep-link support.
  // Accept `?clause=clause_n` on /chat; once the editor has rendered
  // the matching `[data-clause-id]` span, scroll it into view and
  // briefly highlight it so the user sees where the queue/Herald
  // link landed them. Full path-based `/chat/:matter_id` rehydration
  // remains in Cluster E.
  //
  // Iter70 follow-up (Cluster B-9, Feb 06 2026): when `?clause=` arrives
  // on a cold page load and no session is active, auto-attach the user's
  // most recent agent session so the scroll/flash effect can find a
  // [data-clause-id] target. Without this, the deep link silently no-ops
  // on landing and the sharpened Suite 1 Step 9 criterion is unprovable.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const clauseId = params.get('clause');
    if (!clauseId) return undefined;
    if (editorContent) return undefined;     // Editor already populated; main effect below will scroll.
    if (sessionId) return undefined;         // Already attached to a session; let the main effect scroll once it renders.
    if (!session?.access_token) return undefined; // Anonymous deep link — nothing to restore.
    let cancelled = false;
    (async () => {
      try {
        const apiUrl = getBackendBaseUrl();
        const r = await fetch(
          `${apiUrl}/api/v2/agent/sessions?limit=1`,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );
        if (!r.ok) return;
        const data = await r.json();
        const recent = (data.sessions || [])[0];
        if (!recent?.session_id || cancelled) return;
        const envRes = await fetch(
          `${apiUrl}/api/v2/agent/sessions/${encodeURIComponent(recent.session_id)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );
        if (!envRes.ok || cancelled) return;
        const env = await envRes.json();
        if (!cancelled) loadEnvelope(env);
      } catch {
        /* best-effort */
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    const clauseId = params.get('clause');
    if (!clauseId) return undefined;
    if (!editorContent) return undefined; // wait until editor populated
    // Give ContractEditor's render a frame to commit before we hunt
    // for the span.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-clause-id="${clauseId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('bayenah-clause-flash');
      setTimeout(() => el.classList.remove('bayenah-clause-flash'), 2400);
      // Clear the query param so a future re-render doesn't re-scroll.
      const url = new URL(window.location.href);
      url.searchParams.delete('clause');
      window.history.replaceState({}, '', url.toString());
    }, 150);
    return () => clearTimeout(t);
  }, [editorContent]);

  return (
    <div className="h-full w-full" data-testid="agent-preview-page">
      {/* Cluster B-1 (Feb 06 2026): Phase-2 annotation popover. Self-
        * listens to `mouseup` and shows when the selection lies
        * inside a `[data-clause-id]` span on the live tree (the
        * spans are emitted by ContractEditor in view mode via
        * ClauseTaggedMarkdown, driven by the server-emitted
        * `clause_boundaries`). */}
      {sessionId && editorContent && session?.access_token ? (
        <AnnotationPopover
          documentId={editorDraftId || sessionId}
          matterId={sessionId}
          accessToken={session.access_token}
        />
      ) : null}
      <SplitPanelLayout
        leftContent={left}
        rightContent={rightWithToast}
        initialSplit={45}
        harveyTheme={true}
        rightCollapsed={rightCollapsed}
        onRightCollapsedChange={setRightCollapsed}
        rightCollapsedLabel={{ ar: 'المستند', en: 'Document' }}
        // Mobile-only: when there's nothing real to show on the right
        // (no editor content, no draft in flight, no stage progress),
        // omit the pane entirely so the chat gets full height. May-24
        // mobile audit, item #5.
        hideRightOnMobile={!editorContent && !draftInProgress && !draftStageProgress}
      />
      {/* Hidden file picker — both WelcomeHero and ChatComposerToolbar
          trigger this via composerActions.onFiles(). */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.md,.csv,.json,.pdf,.docx,.html,.htm"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          handleLocalFile(f);
          e.target.value = '';
        }}
        data-testid="composer-file-input"
      />
      <VaultPickerModal
        open={vaultOpen}
        onClose={() => setVaultOpen(false)}
        onConfirm={(docs) => ctx.addVaultDocs(docs)}
      />
      <TemplatesPanel
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        onPick={(text) => dispatchComposerPrefill(text)}
      />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

/**
 * UnverifiedPill — small pill rendered below an agent answer when the
 * response was synthesised from general LLM knowledge instead of
 * matched corpus sources. When the agent extracted any canonical law
 * names from the answer, hovering/clicking the pill reveals a tooltip
 * listing them so users can see "this leaned on …" at a glance.
 */
function UnverifiedPill({ isArabic, citedLaws, messageId }) {
  const [open, setOpen] = React.useState(false);
  const hasLaws = Array.isArray(citedLaws) && citedLaws.length > 0;
  const wrapRef = React.useRef(null);

  // Close when clicking outside.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        dir={isArabic ? 'rtl' : 'ltr'}
        className="font-arabic"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          marginTop: '8px',
          padding: '3px 10px',
          background: '#F5F5F3',
          border: '1px solid #E8E8E8',
          borderRadius: '9999px',
          fontSize: '11px',
          color: '#999999',
          cursor: hasLaws ? 'pointer' : 'default',
          fontFamily: 'inherit',
        }}
        title={hasLaws
          ? (isArabic ? `استند إلى: ${citedLaws.join('، ')}` : `Cited: ${citedLaws.join(', ')}`)
          : undefined
        }
        onClick={() => hasLaws && setOpen((v) => !v)}
        aria-haspopup={hasLaws ? 'dialog' : undefined}
        aria-expanded={open}
        data-testid={`agent-preview-unverified-pill-${messageId}`}
      >
        <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#E8E8E8', display: 'inline-block' }} />
        {isArabic
          ? 'مبني على المعرفة العامة — غير مُتحقق من المصادر المفهرسة'
          : 'Based on general knowledge — not verified against indexed sources'}
        {hasLaws && (
          <span aria-hidden="true" style={{ marginInlineStart: '2px', fontSize: '10px', opacity: 0.7 }}>ⓘ</span>
        )}
      </button>
      {hasLaws && open && (
        <div
          role="dialog"
          dir={isArabic ? 'rtl' : 'ltr'}
          className="font-arabic"
          data-testid={`agent-preview-cited-laws-tooltip-${messageId}`}
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            [isArabic ? 'right' : 'left']: 0,
            zIndex: 30,
            background: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
            padding: '10px 12px',
            minWidth: '220px',
            maxWidth: '320px',
            fontSize: '12px',
            color: '#1A1A1A',
          }}
        >
          <div style={{ fontSize: '11px', color: '#999999', marginBottom: '6px' }}>
            {isArabic ? 'استند الذكاء الاصطناعي إلى:' : 'The model leaned on:'}
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {citedLaws.map((law) => (
              <li
                key={law}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', lineHeight: 1.4 }}
                data-testid={`agent-preview-cited-laws-item-${messageId}`}
              >
                <span aria-hidden="true" style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#1A1A1A', display: 'inline-block' }} />
                {law}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Parse a single missing-clause line of the form
 *   N. **بند X:** description text
 * (or `**بند X**`, or with English colon, etc.)
 *
 * Returns { number, name, description } or null if the line doesn't
 * match the expected pattern.
 */
function parseMissingClauseLine(line) {
  // Trim list-marker + number, then look for the bolded clause name.
  const trimmed = String(line || '').trim();
  if (!trimmed) return null;
  // Strip leading "N. " or "- " or "• " bullet
  const stripped = trimmed.replace(/^(?:\d+\.|-|•)\s*/, '');
  // Match **name** or **name:** then optional description
  const m = stripped.match(/^\*\*([^*]+?)\*\*\s*[:：]?\s*(.*)$/);
  if (!m) return null;
  return {
    name: m[1].trim().replace(/[:：]\s*$/, ''),  // drop trailing colon if any
    description: (m[2] || '').trim(),
  };
}

/**
 * Render the review/compliance answer text with two enhancements:
 *   1. inline `[n]` citation markers (delegated to renderWithCitations)
 *   2. a "+ أضف هذا البند" button after every list item inside the
 *      `## البنود الناقصة` (review) or `## الفجوات المُحدَّدة`
 *      (compliance) section
 *
 * Strategy: split the text into three slabs at the first matching
 * heading and the next `##` heading. The middle slab is rendered as a
 * structured list with buttons; the surrounding slabs use the normal
 * citation-aware renderer.
 *
 * If no matching section is present we fall back to the standard
 * citation-aware renderer.
 *
 * @param frameworksChecked Optional array of Arabic framework names
 *        rendered into the compliance prefill prompt.
 */
function renderReviewWithButtons(text, onCitationClick, onAddClause, citationCount = 0, frameworksChecked = []) {
  const raw = String(text || '');
  if (!raw) return null;

  // Match either the review or compliance section heading.
  const headingRe = /(^|\n)##\s*(?:البنود\s*الناقصة|الفجوات\s*المُحدَّدة|الفجوات\s*المحددة|Missing\s*Clauses|Identified\s*Gaps)\s*(?:\n|$)/m;
  const m = raw.match(headingRe);
  if (!m) {
    return renderWithCitations(raw, onCitationClick, citationCount);
  }
  const headingStart = m.index + (m[1] === '\n' ? 1 : 0);
  const afterHeading = m.index + m[0].length;

  // Determine variant from the matched heading text. Compliance gap
  // rows pre-fill a different prompt that namechecks the relevant
  // regulatory framework(s).
  const headingText = m[0].replace(/^[\s\n]*##\s*/, '').trim();
  const isCompliance = /الفجوات|Identified\s*Gaps/i.test(headingText);

  // The section ends at the next `## ` heading or end of text.
  const tail = raw.slice(afterHeading);
  const nextHeading = tail.search(/(^|\n)##\s+/m);
  const sectionEnd = nextHeading === -1 ? raw.length : afterHeading + nextHeading + (tail[nextHeading] === '\n' ? 1 : 0);

  const before = raw.slice(0, headingStart);
  const sectionBody = raw.slice(afterHeading, sectionEnd);
  const after = raw.slice(sectionEnd);

  // Each item starts with a numbered list marker on its own line.
  // Sub-bullets (e.g. `   - دليل: ...`) are part of the same item, so
  // we keep them with the parent until the next numbered marker.
  const lines = sectionBody.split(/\n/);
  const items = [];
  let currentNumbered = null;
  for (const ln of lines) {
    if (/^\s*\d+\.\s/.test(ln)) {
      if (currentNumbered) items.push(currentNumbered);
      currentNumbered = { head: ln, sub: [] };
    } else if (currentNumbered && /^\s+(?:-|•)\s/.test(ln)) {
      currentNumbered.sub.push(ln);
    } else if (ln.trim()) {
      // Stray line outside the structured pattern.
      if (currentNumbered) {
        currentNumbered.sub.push(ln);
      } else {
        items.push({ head: ln, sub: [] });
      }
    }
  }
  if (currentNumbered) items.push(currentNumbered);

  return (
    <>
      {before && renderWithCitations(before, onCitationClick, citationCount)}
      <div style={{ marginTop: '8px', marginBottom: '4px', fontWeight: 600 }}>
        {headingText}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((item, i) => {
          const parsed = parseMissingClauseLine(item.head);
          if (!parsed) {
            return (
              <div key={`mc-raw-${i}`} style={{ padding: '4px 0' }}>
                {renderWithCitations(item.head, onCitationClick, citationCount)}
              </div>
            );
          }
          const { name, description } = parsed;
          return (
            <div
              key={`mc-${i}`}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
                padding: '10px 0',
                borderBottom: '1px solid #F0F0F0',
              }}
              data-testid={isCompliance ? `compliance-gap-${i}` : `review-missing-clause-${i}`}
            >
              <div style={{ fontSize: '13px', color: '#111111', flex: 1, lineHeight: 1.6 }}>
                <strong>{name}</strong>
                {description ? <span style={{ color: '#444444' }}>{`: ${description}`}</span> : null}
                {item.sub.length > 0 && (
                  <div style={{ marginTop: '4px' }}>
                    {item.sub.map((sub, si) => (
                      <div key={`sub-${si}`} style={{ fontSize: '12px', color: '#666666', lineHeight: 1.6 }}>
                        {renderWithCitations(sub.replace(/^\s+(?:-|•)\s*/, '— '), onCitationClick, citationCount)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onAddClause?.(name, isCompliance, frameworksChecked)}
                className="font-arabic"
                style={{
                  padding: '4px 12px',
                  background: '#FFFFFF',
                  border: '1px solid #E8E8E8',
                  borderRadius: '9999px',
                  fontSize: '11px',
                  color: '#444444',
                  cursor: 'pointer',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#111111';
                  e.currentTarget.style.color = '#FFFFFF';
                  e.currentTarget.style.borderColor = '#111111';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#FFFFFF';
                  e.currentTarget.style.color = '#444444';
                  e.currentTarget.style.borderColor = '#E8E8E8';
                }}
                data-testid={isCompliance ? `compliance-add-clause-btn-${i}` : `review-add-clause-btn-${i}`}
                aria-label={`أضف بند ${name}`}
              >
                + أضف هذا البند
              </button>
            </div>
          );
        })}
      </div>
      {after && renderWithCitations(after, onCitationClick, citationCount)}
    </>
  );
}

function mapActionToTask(action) {
  if (!action) return null;
  // Sprint 4 (May 2026): 'draft' now routes to the full multi-agent
  // pipeline (Scribe → Examiner + Regis → Conductor) via the v2/agent
  // SSE path with override_task='full_draft'. This replaces the
  // legacy /api/draft-jobs async-job path for chat-initiated drafts.
  // Composer 'review' continues to route through full_review (Examiner
  // + Regis + Counsel + Conductor) — same backend pattern.
  const map = {
    review:     'full_review',
    draft:      'full_draft',
    compliance: 'gap_analysis',
    improve:    'improve',
  };
  return map[action] || action;
}

// Sprint 4: typed-message draft intent detection (separate from the
// legacy draftIntentDetector which routed to /api/draft-jobs). When
// the user types a message containing both a drafting verb and a
// contract noun, the composer should set override_task='full_draft'
// so the multi-agent pipeline kicks in even without a Draft button click.
const SPRINT4_DRAFT_KEYWORDS_EN = ['draft', 'write', 'create', 'generate', 'prepare'];
const SPRINT4_DRAFT_KEYWORDS_AR = ['صغ', 'اكتب', 'أنشئ', 'اعداد', 'أعد', 'قم بصياغة'];
const SPRINT4_CONTRACT_NOUNS_EN = ['agreement', 'contract', 'nda', 'sla', 'partnership', 'policy'];
const SPRINT4_CONTRACT_NOUNS_AR = ['اتفاقية', 'عقد', 'سياسة', 'شراكة', 'نموذج'];

function isFullDraftIntent(message) {
  const lower = (message || '').toLowerCase();
  const hasKeyword = [...SPRINT4_DRAFT_KEYWORDS_EN, ...SPRINT4_DRAFT_KEYWORDS_AR]
    .some((k) => lower.includes(k.toLowerCase()));
  const hasNoun = [...SPRINT4_CONTRACT_NOUNS_EN, ...SPRINT4_CONTRACT_NOUNS_AR]
    .some((n) => lower.includes(n.toLowerCase()));
  return hasKeyword && hasNoun;
}

/**
 * Walk an answer string and replace inline `[1]`, `[2]`, … citation
 * markers with clickable superscript spans. Out-of-range markers (n
 * greater than the citation count or n <= 0) render as plain
 * non-clickable text so we don't dangle dead clicks for users.
 *
 * Splitting on the regex preserves the surrounding text in order;
 * everything that does not match the marker pattern is rendered as a
 * plain `<span>` so RTL flow / line wrapping continues to work
 * naturally (the parent container is `whitespace-pre-wrap`).
 */
function renderWithCitations(text, onCitationClick, citationCount = 0, isArabic = false, onPolicyClick = null) {
  if (!text) return null;
  // Capture statutory [N], Ledger [L#] and Pinned [P#] markers in one pass
  // so each can be rendered with its own visual treatment.
  const parts = String(text).split(/(\[\d+\]|\[[LP]\d+\])/g);
  return parts.map((part, i) => {
    // Ledger / Pinned company-doc citation → tinted "Your Policy" pill.
    // Distinct color (policy blue) from statutory citations (neutral
    // grey). The marker letter (L=Ledger, P=Pinned) and number stay
    // visible inside the pill so reviewers can still cross-reference.
    const lp = part.match(/^\[([LP])(\d+)\]$/);
    if (lp) {
      const kind = lp[1];
      const n = parseInt(lp[2], 10);
      const marker = `${kind}${n}`;
      const label = isArabic ? 'سياستك' : 'Your Policy';
      const interactive = typeof onPolicyClick === 'function';
      return (
        <span
          key={`policy-cite-${i}`}
          data-testid={`agent-preview-policy-marker-${marker}`}
          dir={isArabic ? 'rtl' : 'ltr'}
          {...(interactive ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onPolicyClick(marker),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPolicyClick(marker);
              }
            },
          } : {})}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '1px 6px 1px 5px',
            background: 'rgba(37, 99, 235, 0.08)',
            border: '1px solid rgba(37, 99, 235, 0.25)',
            borderRadius: '9999px',
            fontSize: '10px',
            lineHeight: '14px',
            color: '#2563EB',
            fontWeight: 500,
            verticalAlign: 'baseline',
            margin: '0 2px',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
            cursor: interactive ? 'pointer' : 'default',
            transition: 'background-color 0.15s, border-color 0.15s',
          }}
          title={isArabic ? `عرض نص السياسة (${marker})` : `View policy text (${marker})`}
          onMouseEnter={interactive ? (e) => {
            e.currentTarget.style.background = 'rgba(37, 99, 235, 0.16)';
            e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.50)';
          } : undefined}
          onMouseLeave={interactive ? (e) => {
            e.currentTarget.style.background = 'rgba(37, 99, 235, 0.08)';
            e.currentTarget.style.borderColor = 'rgba(37, 99, 235, 0.25)';
          } : undefined}
        >
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '13px',
            height: '13px',
            padding: '0 3px',
            background: '#2563EB',
            color: '#FFFFFF',
            borderRadius: '9999px',
            fontSize: '8.5px',
            fontWeight: 600,
            lineHeight: '13px',
          }}>
            {kind}{n}
          </span>
          <span>{label}</span>
        </span>
      );
    }
    const m = part.match(/^\[(\d+)\]$/);
    if (m) {
      const n = parseInt(m[1], 10);
      const idx = n - 1;
      const inRange = n > 0 && idx < citationCount;
      if (inRange) {
        return (
          <span
            key={`cite-${i}`}
            role="button"
            tabIndex={0}
            onClick={() => onCitationClick?.(idx)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCitationClick?.(idx);
              }
            }}
            data-testid={`agent-preview-citation-marker-${n}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '16px',
              height: '16px',
              background: '#F5F5F3',
              border: '1px solid #E8E8E8',
              borderRadius: '3px',
              fontSize: '9px',
              color: '#666666',
              cursor: 'pointer',
              verticalAlign: 'super',
              margin: '0 1px',
              fontWeight: 500,
              transition: 'background-color 0.1s, color 0.1s, border-color 0.1s',
              userSelect: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#111111';
              e.currentTarget.style.color = '#FFFFFF';
              e.currentTarget.style.borderColor = '#111111';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#F5F5F3';
              e.currentTarget.style.color = '#666666';
              e.currentTarget.style.borderColor = '#E8E8E8';
            }}
          >
            {n}
          </span>
        );
      }
      // Out-of-range marker — keep as plain text.
      return <span key={`cite-${i}`}>{part}</span>;
    }
    // Sep 2026 (RTL-markdown fix): render **bold** and *italic* /
    // _italic_ inline. Arabic bubbles previously displayed literal
    // asterisks because renderWithCitations returned raw text spans.
    // The pattern is boundary-aware so Arabic punctuation (، ؛ .)
    // doesn't break bold spans.
    return <span key={`text-${i}`}>{renderInlineEmphasis(part)}</span>;
  });
}

// Splits **bold** / *italic* / _italic_ sequences into <strong>/<em>.
// Kept ASCII-only pattern so it works identically in RTL text.
function renderInlineEmphasis(text) {
  if (!text) return text;
  const RE = /(\*\*(?=\S)([\s\S]+?)(?<=\S)\*\*|(?<!\w)\*(?=\S)([^*\n]+?)(?<=\S)\*(?!\w)|(?<!\w)_(?=\S)([^_\n]+?)(?<=\S)_(?!\w))/g;
  const out = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = RE.exec(text)) !== null) {
    if (m.index > lastIdx) out.push(text.slice(lastIdx, m.index));
    if (m[2] !== undefined) {
      out.push(<strong key={`b-${key++}`}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      out.push(<em key={`i-${key++}`}>{m[3]}</em>);
    } else if (m[4] !== undefined) {
      out.push(<em key={`u-${key++}`}>{m[4]}</em>);
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out.length ? out : text;
}

/**
 * ChatComposerToolbar — mirrors the WelcomeHero's 5 composer buttons for
 * use above the active-chat SmartInput. Identical behaviour: vault →
 * modal, doc-type → popover, files → hidden picker, templates → side
 * panel, customise → popover.
 */
function ChatComposerToolbar({
  isArabic,
  ctx,
  composerActions,
  anchor = 'chat',
  docTypeAnchor,
  customizeAnchor,
  closeDocType,
  closeCustomize,
}) {
  const btnStyle = {
    display: 'flex', alignItems: 'center', gap: '6px',
    fontSize: '12px', color: '#666666',
    background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 0',
  };
  return (
    /* Jul 2026 mobile fix: this utility row (Vault / Document type /
       Files / Templates / Customize) made the composer so tall on
       phones that the send button fell behind the browser's bottom
       toolbar. Hidden below md — the attach icon covers the mobile
       upload path; the full row remains on desktop. */
    <div
      className={cn('hidden md:flex items-center gap-3 mb-1.5 flex-wrap', isArabic && 'flex-row-reverse')}
      data-testid={`chat-composer-toolbar-${anchor}`}
    >
      <button type="button" onClick={composerActions.onVault} className="font-arabic" style={btnStyle} data-testid="chat-action-vault">
        <FolderIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
        {isArabic ? 'الخزانة' : 'Vault'}
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => composerActions.onDocType(anchor)}
          className="font-arabic"
          style={btnStyle}
          data-testid="chat-action-doc-type"
        >
          <FileTextIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
          {isArabic ? (ctx.documentType ? ctx.documentType.label : 'نوع المستند') : (ctx.documentType ? ctx.documentType.en : 'Document type')}
        </button>
        {docTypeAnchor === anchor && (
          <div className="absolute" style={{ bottom: '100%', marginBottom: '6px', [isArabic ? 'right' : 'left']: 0 }}>
            <DocTypePopover
              isArabic={isArabic}
              onPick={(t) => ctx.setDocumentType(t)}
              onClose={closeDocType}
            />
          </div>
        )}
      </div>
      <button type="button" onClick={composerActions.onFiles} className="font-arabic" style={btnStyle} data-testid="chat-action-files">
        <PaperclipIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
        {isArabic ? 'ملفات ومصادر' : 'Files & sources'}
      </button>
      <button type="button" onClick={composerActions.onTemplates} className="font-arabic" style={btnStyle} data-testid="chat-action-templates">
        <ListIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
        {isArabic ? 'نماذج' : 'Templates'}
      </button>
      <div className="relative">
        <button
          type="button"
          onClick={() => composerActions.onCustomize(anchor)}
          className="font-arabic"
          style={btnStyle}
          data-testid="chat-action-customize"
        >
          <SlidersIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
          {isArabic ? 'تخصيص' : 'Customize'}
        </button>
        {customizeAnchor === anchor && (
          <div className="absolute" style={{ bottom: '100%', marginBottom: '6px', [isArabic ? 'right' : 'left']: 0 }}>
            <CustomizePopover
              isArabic={isArabic}
              settings={ctx.settings}
              onChange={ctx.updateSetting}
              onReset={ctx.resetSettings}
              onClose={closeCustomize}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// Fix 3 (Feb 2026) — Chat bubble shows a short summary for review /
// full_review answers; the full structured report stays in the right
// pane editor only. Without this, the same long Markdown rendered in
// both places (duplication bug).
// Extract the "Executive Summary" body from a full review and return BOTH
// the body and the pointer line as separate strings.  The bubble renders
// the body, then the AgentStatusChips strip, then the pointer — so the
// chips physically appear "below the summary text, above the document-
// panel pointer" as required.
function extractReviewSummary(text, isArabic) {
  if (!text) return { body: '', pointer: '' };
  const trimmed = text.trim();
  // Try to locate the Executive Summary section. Match Arabic + English
  // headings and any reasonable variants the LLMs emit.
  const headingRe = /(?:^|\n)#{2,3}\s*(?:executive\s+summary|summary|الملخص\s*التنفيذي|ملخص\s*تنفيذي|verdict|الخلاصة)\s*\n+/i;
  const m = headingRe.exec(trimmed);
  let body;
  if (m) {
    const start = m.index + m[0].length;
    // Stop at the next ##/### heading.
    const nextHeading = /\n#{2,3}\s/.exec(trimmed.slice(start));
    const end = nextHeading ? start + nextHeading.index : trimmed.length;
    body = trimmed.slice(start, end).trim();
  } else {
    // No exec-summary heading — fall back to the first paragraph.
    body = trimmed.split(/\n\s*\n/, 1)[0] || trimmed;
  }
  // Strip any inline citation markers we don't want in the bubble
  // (the right panel keeps them). Keep [L#] / [P#] for Ledger context.
  body = body.replace(/\s+/g, ' ').trim();
  // Cap length so the bubble stays roughly one short paragraph.
  const MAX = 480;
  if (body.length > MAX) {
    const cut = body.lastIndexOf('. ', MAX);
    body = (cut > MAX - 80 ? body.slice(0, cut + 1) : body.slice(0, MAX)) + '…';
  }
  const pointer = isArabic
    ? 'التقرير الكامل متاح في لوحة المستند على اليسار.'
    : 'Full review available in the document panel.';
  return { body, pointer };
}


function Bubble({ message, isArabic, progressPhase, progressStartedAt, onRetryCompliance, sequencerActive = false }) {
  const isUser = message.role === 'user';
  const draftOutput = !isUser && message.meta?.output?.draft_text ? message.meta.output : null;
  // Jul 2026: the phase estimator hides while the sequencer strip is
  // active — one progress surface at a time.
  const showProgress = !isUser && message.isStreaming && progressPhase && !sequencerActive;
  // Honest provenance: when the answer was synthesised from general LLM
  // knowledge (corpus returned 0 hits), surface a small "unverified" pill
  // beneath the bubble so users know it isn't corpus-grounded. We only
  // render it on completed assistant answers.
  const showUnverifiedPill = (
    !isUser
    && !message.isStreaming
    && message.meta?.output?.corpus_verified === false
    && Boolean(message.meta?.output?.answer)
  );
  // Sources badge: when the answer IS corpus-verified and we have at
  // least one source (corpus or web), show a clickable badge that
  // opens a side-sheet listing the citations.
  const sourcesList = Array.isArray(message.meta?.sources) ? message.meta.sources : [];
  const showSourcesBadge = (
    !isUser
    && !message.isStreaming
    && sourcesList.length > 0
    && message.meta?.output?.corpus_verified !== false
  );
  const [sourcesOpen, setSourcesOpen] = React.useState(false);
  const [highlightedCitation, setHighlightedCitation] = React.useState(null);

  // Policy side-sheet state — opens when the reader clicks a [L#] / [P#]
  // pill in the bubble. Decoupled from the statutory `sourcesOpen` flag
  // so both panels can be toggled independently.
  const policyDocs = (message.meta?.output?.policy_docs && typeof message.meta.output.policy_docs === 'object')
    ? message.meta.output.policy_docs
    : { P: [], L: [] };
  const hasPolicyDocs = (
    (Array.isArray(policyDocs.P) && policyDocs.P.length > 0)
    || (Array.isArray(policyDocs.L) && policyDocs.L.length > 0)
  );
  const [policyOpen, setPolicyOpen] = React.useState(false);
  const [highlightedPolicyMarker, setHighlightedPolicyMarker] = React.useState(null);

  // Click handler for inline [L#] / [P#] pills.
  const onPolicyClick = React.useCallback((marker) => {
    setPolicyOpen(true);
    setHighlightedPolicyMarker(marker);
    // Close the statutory sources panel if it was open so the layout
    // doesn't try to render two competing side-sheets at once.
    setSourcesOpen(false);
  }, []);

  // Click handler for inline [n] markers — opens the side-sheet and
  // scrolls to citation card n-1.
  const onCitationClick = React.useCallback((idx) => {
    setSourcesOpen(true);
    setHighlightedCitation(idx);
    setPolicyOpen(false);
  }, []);

  // Click handler for "أضف هذا البند" buttons in the review's missing-
  // clauses section AND the compliance audit's gaps section. Pre-fills
  // the chat input with a draft prompt and focuses it. Same
  // `bayenah:composer-prefill` event the TemplatesPanel uses, so both
  // WelcomeHero and SmartInput receive it.
  //
  // For compliance gaps we name-check the relevant frameworks so the
  // drafter respects the right regulatory context (e.g. social
  // insurance reqs land under the social-insurance framework, not just
  // the generic "Saudi regulations").
  const onAddClause = React.useCallback((clauseName, isCompliance, frameworksChecked) => {
    const cleaned = String(clauseName || '').replace(/^\s*بند\s+/, '').trim();
    let frameworkRef = 'الأنظمة السعودية';
    if (isCompliance && Array.isArray(frameworksChecked) && frameworksChecked.length > 0) {
      // De-duplicate (the joiner doesn't, and the LLM occasionally
      // emits the same framework twice for partial coverage).
      const unique = Array.from(new Set(frameworksChecked.filter(Boolean)));
      frameworkRef = unique.join(' و ');
    }
    const text = `أضف بند ${cleaned} إلى العقد بصيغة قانونية سعودية رسمية ومتوافقة مع ${frameworkRef}`;
    window.dispatchEvent(new CustomEvent('bayenah:composer-prefill', { detail: { text } }));
  }, []);

  // Detect whether this is a review/compliance answer so we can light
  // up the missing-clause / gap buttons.
  const reviewSectionRe = /(^|\n)##\s*(?:البنود\s*الناقصة|الفجوات\s*المُحدَّدة|الفجوات\s*المحددة|Missing\s*Clauses|Identified\s*Gaps)\s*(?:\n|$)/m;
  const decisionTask = message.meta?.decision?.task;
  const isFullReview = decisionTask === 'full_review';
  const isReview = (
    !isUser
    && !message.isStreaming
    && (
      decisionTask === 'review'
      || decisionTask === 'gap_analysis'
      || isFullReview
      || reviewSectionRe.test(message.content || '')
    )
  );
  // Fix 3 — for any review answer (single-agent or Sequencer fan-out),
  // render only the short summary in the bubble. The full structured
  // report flows to the right-pane editor via the parent useEffect.
  const summaryParts = isReview ? extractReviewSummary(message.content || '', isArabic) : null;
  const bubbleText = (
    isUser
      ? (message.content || (message.isStreaming ? '...' : ''))
      : isReview && summaryParts && summaryParts.body
        ? summaryParts.body
        : (message.content || (message.isStreaming ? '...' : ''))
  );
  const bubblePointer = isReview && summaryParts ? summaryParts.pointer : '';
  const frameworksChecked = Array.isArray(message.meta?.output?.frameworks_checked)
    ? message.meta.output.frameworks_checked
    : [];

  // Per-agent status chips for full_review (Sequencer fan-out) responses.
  // Names are ALWAYS rendered in English — even when the interface is
  // in Arabic mode — so the user-facing label matches the technical
  // agent identity 1:1.  Read flags from the result envelope:
  //   examiner_success / regis_success / counsel_success / conductor_success
  const showAgentChips = (
    !isUser
    && !message.isStreaming
    && isFullReview
    && message.meta?.output
    && (
      'examiner_success' in (message.meta.output || {})
      || 'regis_success' in (message.meta.output || {})
      || 'counsel_success' in (message.meta.output || {})
      || 'conductor_success' in (message.meta.output || {})
    )
  );
  const agentChips = showAgentChips ? [
    { id: 'examiner',  name: 'Examiner',  ok: Boolean(message.meta.output.examiner_success) },
    { id: 'regis',     name: 'Regis',     ok: Boolean(message.meta.output.regis_success) },
    { id: 'counsel',   name: 'Counsel',   ok: Boolean(message.meta.output.counsel_success) },
    // P0.2 (Jun-09 2026): Sentinel appears as a chip ONLY when it
    // actually ran (i.e. the org had ≥1 internal policy). When
    // skipped, the chip is omitted and the adoption-pull footer
    // note (`sentinelSkippedNote` below) renders instead so the GC
    // sees there is a capability they're not yet using.
    ...(message.meta.output.sentinel_ran
      ? [{ id: 'sentinel', name: 'Sentinel', ok: Boolean(message.meta.output.sentinel_success) }]
      : []),
    { id: 'conductor', name: 'Conductor', ok: Boolean(message.meta.output.conductor_success) },
    // Jul 2026 (calm-failure policy): a delivered report never wears
    // red badges — only contributing agents are shown. Any reduced
    // dimension is disclosed in the report's own text (and, for Regis,
    // drives the retry CTA below). Truth preserved, alarm removed.
  ].filter((c) => c.ok) : [];

  // P0.2 — Adoption pull: when Sentinel skipped because the org has
  // no internal policies in its vault, render a quiet footer note
  // pointing them at the capability. We deliberately render this
  // only for the `no_internal_policies` reason (vs. e.g. anonymous
  // user, vault error) so the message stays accurate.
  const sentinelSkippedNote = (
    !isUser
    && !message.isStreaming
    && isFullReview
    && message.meta?.output
    && message.meta.output.sentinel_ran === false
    && message.meta.output.sentinel_skipped_reason === 'no_internal_policies'
  );

  // Regis-failure CTA — when the orchestrator envelope flags
  // regis_success === false, render an inline "Run Compliance Check"
  // button that re-fires the same document through the compliance
  // pipeline (override_task='gap_analysis'), without forcing the user
  // to scroll up and re-upload.  Only renders for full_review responses.
  const regisFailed = (
    !isUser
    && !message.isStreaming
    && isFullReview
    && message.meta?.output
    && message.meta.output.regis_success === false
  );

  return (
    <div className={cn('flex flex-col gap-1.5', isUser && 'items-end')} data-testid={`agent-preview-bubble-${message.id}`}>
      {draftOutput ? (
        <DraftCompletionBubble output={draftOutput} isArabic={isArabic} />
      ) : (
        <div
          className={cn('px-3.5 py-2.5 text-sm whitespace-pre-wrap', isArabic && 'text-right')}
          data-testid={`agent-preview-bubble-text-${message.id}`}
          style={isUser
            ? { background: '#111111', color: '#FFFFFF', borderRadius: '12px', maxWidth: '85%' }
            : { background: 'var(--surface)', border: 'var(--border-default)', color: 'var(--text-primary)', borderRadius: '12px', maxWidth: '95%' }
          }
        >
          {isUser
            ? bubbleText
            : isReview
              ? renderWithCitations(bubbleText, onCitationClick, sourcesList.length, isArabic, hasPolicyDocs ? onPolicyClick : null)
              : renderWithCitations(message.content || (message.isStreaming ? '...' : ''), onCitationClick, sourcesList.length, isArabic, hasPolicyDocs ? onPolicyClick : null)
          }
          {/* Agent status chips appear BELOW the summary text and ABOVE
             the "Full review available in the document panel" pointer.
             Names are always English, even when isArabic. */}
          {showAgentChips && (
            <div
              dir="ltr"
              className="flex flex-wrap gap-1.5 mt-3"
              data-testid={`agent-status-chips-${message.id}`}
            >
              {agentChips.map((chip) => (
                <span
                  key={chip.id}
                  data-testid={`agent-status-chip-${chip.id}`}
                  data-agent-name={chip.name}
                  data-agent-ok={chip.ok ? 'true' : 'false'}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 9px',
                    borderRadius: '9999px',
                    fontSize: '11px',
                    lineHeight: '1.4',
                    fontFamily: 'inherit',
                    background: '#FFFFFF',
                    border: '1px solid #E8E8E8',
                    color: '#444444',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span>{chip.name}</span>
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '13px',
                      height: '13px',
                      borderRadius: '9999px',
                      fontSize: '10px',
                      fontWeight: 700,
                      lineHeight: 1,
                      background: chip.ok ? '#16A34A' : '#DC2626',
                      color: '#FFFFFF',
                    }}
                  >
                    {chip.ok ? '✓' : '✗'}
                  </span>
                </span>
              ))}
            </div>
          )}
          {/* P0.2 (Jun-09 2026): Sentinel skipped-note. Renders only
              when the full-review envelope reports the cross-check
              was skipped because the org's vault has no internal
              policies. Adoption-pull copy — show the GC there's a
              capability they're not using and how to activate it. */}
          {sentinelSkippedNote && (
            <div
              dir={isArabic ? 'rtl' : 'ltr'}
              data-testid={`sentinel-skipped-note-${message.id}`}
              style={{
                marginTop: '12px',
                padding: '8px 11px',
                background: '#F7F7F5',
                border: '1px solid #E8E8E8',
                borderRadius: '6px',
                fontSize: '11px',
                lineHeight: '1.55',
                color: '#666666',
              }}
            >
              {isArabic
                ? 'تم تخطي المقارنة مع سياسات الشركة الداخلية — ارفع دليل السياسات الخاص بشركتك لتفعيل هذه الميزة.'
                : 'Internal policy cross-check skipped — upload your playbook to enable.'}
            </div>
          )}
          {/* Failed-Regis CTA: actionable note + button.  Sits between
              the agent chips and the pointer line.  Note: the
              `regis_success === false` flag is set by the orchestrator
              envelope only on full_review responses, so this never
              renders for single-agent compliance checks. */}
          {regisFailed && (
            <div
              dir={isArabic ? 'rtl' : 'ltr'}
              data-testid={`regis-retry-note-${message.id}`}
              style={{
                marginTop: '12px',
                padding: '10px 12px',
                border: '1px solid #FCD9D6',
                borderLeft: isArabic ? undefined : '3px solid #DC2626',
                borderRight: isArabic ? '3px solid #DC2626' : undefined,
                background: '#FEF7F6',
                borderRadius: '6px',
                fontSize: '12px',
                lineHeight: '1.55',
                color: '#5D1A18',
              }}
            >
              <div style={{ marginBottom: '6px' }}>
                {isArabic
                  ? 'لم يكن تحليل الامتثال متاحاً في هذه المراجعة لأسباب فنية.'
                  : 'Compliance analysis was not available for this review due to a technical issue.'}
              </div>
              <button
                type="button"
                data-testid="regis-retry-compliance-btn"
                onClick={() => onRetryCompliance?.(message.id)}
                style={{
                  marginTop: '4px',
                  padding: '5px 12px',
                  fontSize: '11px',
                  fontFamily: 'inherit',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  background: '#FFFFFF',
                  cursor: 'pointer',
                  color: '#374151',
                  whiteSpace: 'nowrap',
                }}
              >
                {isArabic ? 'تشغيل فحص الامتثال' : 'Run Compliance Check'}
              </button>
            </div>
          )}
          {/* Pointer line — separated from the body so it sits AFTER the chips.
              Sep 2026 (error-copy fix): only render when the review actually
              produced structured content in the document panel. When the
              envelope was empty (failure / cancellation) or a plain
              answer bubble, the "Full review available" line was a lie. */}
          {!isUser && isReview && bubblePointer && Boolean(
            message.meta?.output?.review_text
            || message.meta?.output?.draft_text
            || message.meta?.output?.report
          ) && (
            <div
              className={cn('mt-3 text-sm', isArabic && 'text-right')}
              data-testid={`agent-preview-bubble-pointer-${message.id}`}
              style={{ color: 'var(--text-secondary, #666666)' }}
            >
              {bubblePointer}
            </div>
          )}
          {showProgress && (
            <ProgressEstimator
              phase={progressPhase}
              isArabic={isArabic}
              startedAt={progressStartedAt}
            />
          )}
        </div>
      )}
      {!isUser && Array.isArray(message.meta?.reasoning_steps) && message.meta.reasoning_steps.length > 0
        && !(sequencerActive && message.isStreaming) && (
        <ReasoningPanel
          steps={message.meta.reasoning_steps}
          isStreaming={Boolean(message.isStreaming)}
          defaultOpen={Boolean(message.isStreaming)}
        />
      )}
      {showUnverifiedPill && (
        <UnverifiedPill
          isArabic={isArabic}
          citedLaws={Array.isArray(message.meta?.output?.cited_laws) ? message.meta.output.cited_laws : []}
          messageId={message.id}
        />
      )}
      {/* Jul 2026 — confidence/abstention pill. Backed by retrieval
          quality (research_agent grades every answer). High confidence
          shows nothing (the sources badge already carries the trust);
          medium/low get an explicit honesty marker. */}
      {/* Sep 2026 (abstention-pill fix): the previous guard required
          `corpus_verified` to be truthy, which is exactly false on an
          abstention (0 hits → not corpus-verified). Confidence pill
          must render for every medium/low answer, corpus_verified or
          not — the pill IS the honesty marker on an abstention. */}
      {!isUser && !message.isStreaming
        && ['medium', 'low'].includes(message.meta?.output?.confidence) && (
        <span
          dir={isArabic ? 'rtl' : 'ltr'}
          data-testid={`confidence-pill-${message.id}`}
          className="font-arabic"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
            fontSize: 12, padding: '3px 10px', borderRadius: 999,
            background: message.meta.output.confidence === 'medium' ? '#FAEEDA' : '#FCEBEB',
            color: message.meta.output.confidence === 'medium' ? '#854F0B' : '#A32D2D',
            border: '0.5px solid rgba(0,0,0,0.06)',
          }}
        >
          <span aria-hidden style={{
            width: 6, height: 6, borderRadius: '50%',
            background: message.meta.output.confidence === 'medium' ? '#BA7517' : '#A32D2D',
          }} />
          {message.meta.output.confidence === 'medium'
            ? (isArabic ? 'ثقة متوسطة — تحقّق من المصادر' : 'Medium confidence — verify sources')
            : (isArabic ? 'ثقة منخفضة — يُنصح بمراجعة مختص' : 'Low confidence — consult counsel')}
        </span>
      )}
      {showSourcesBadge && (
        <button
          type="button"
          onClick={() => setSourcesOpen(true)}
          dir={isArabic ? 'rtl' : 'ltr'}
          className="font-arabic"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '5px',
            marginTop: '8px',
            padding: '3px 10px',
            background: '#FFFFFF',
            border: '1px solid #E8E8E8',
            borderRadius: '9999px',
            fontSize: '11px',
            color: '#444444',
            cursor: 'pointer',
            transition: 'border-color 0.15s, color 0.15s',
            fontFamily: 'inherit',
            width: 'fit-content',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#AAAAAA'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E8E8'; }}
          data-testid={`agent-preview-sources-badge-${message.id}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <rect x="1" y="1" width="7" height="9" rx="1" stroke="#444" strokeWidth="1.2" />
            <path d="M3 4h5M3 6h5M3 8h3" stroke="#444" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          {isArabic
            ? `${sourcesList.length} مصدر قانوني مُستخدم`
            : `${sourcesList.length} legal source${sourcesList.length === 1 ? '' : 's'} used`}
        </button>
      )}
      <SourcesSideSheet
        isOpen={sourcesOpen}
        onClose={() => { setSourcesOpen(false); setHighlightedCitation(null); }}
        citations={sourcesList}
        isArabic={isArabic}
        highlightedCitation={highlightedCitation}
      />
      <PolicySideSheet
        isOpen={policyOpen}
        onClose={() => { setPolicyOpen(false); setHighlightedPolicyMarker(null); }}
        policyDocs={policyDocs}
        isArabic={isArabic}
        highlightedMarker={highlightedPolicyMarker}
      />
      {!isUser && !message.isStreaming && Array.isArray(message.meta?.follow_ups) && message.meta.follow_ups.length > 0 && (
        <FollowUpSuggestions
          suggestions={message.meta.follow_ups}
          onSelect={(s) => window.dispatchEvent(new CustomEvent('bayenah:follow-up-selected', { detail: { suggestion: s } }))}
        />
      )}
    </div>
  );
}

// ── Welcome Hero — empty state with input as the centered hero element.
function WelcomeHero({
  isArabic,
  ctx,
  composerActions,
  docTypeAnchor,
  customizeAnchor,
  closeDocType,
  closeCustomize,
  onSubmit,
}) {
  const [value, setValue] = React.useState('');
  const textareaRef = React.useRef(null);

  // ── Cluster H.5 (Feb 2026) ────────────────────────────────────
  // The first-session welcome modal that lived here has been removed.
  // Its functional role (orientation hints + three example prompts) is
  // replaced by the DashboardHomeHeader (logo + time-aware greeting +
  // activity stats) and the RecentMattersStrip + TodayPanel below.
  const { user } = useAuth();
  const rawName = user?.name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const firstNameRaw = (rawName || '').trim().split(/\s+/)[0] || '';
  const displayName = firstNameRaw
    ? firstNameRaw.charAt(0).toUpperCase() + firstNameRaw.slice(1).toLowerCase()
    : '';

  // Receive prefill text broadcast from TemplatesPanel.
  React.useEffect(() => {
    const onPrefill = (e) => {
      const t = e.detail?.text;
      if (typeof t === 'string') {
        setValue(t);
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    };
    window.addEventListener('bayenah:composer-prefill', onPrefill);
    return () => window.removeEventListener('bayenah:composer-prefill', onPrefill);
  }, []);

  const submit = () => {
    const text = value.trim();
    if (!text) return;
    onSubmit(text);
    setValue('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const fillTask = (text) => {
    setValue(text);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const tasks = isArabic
    ? [
        { id: 'review',     title: 'مراجعة عقد',           type: 'اكتشف المخاطر في عقد مرفوع',                action: 'ارفع العقد وسأحلل البنود، أحدد المخاطر، وأكشف البنود المفقودة وفق الأنظمة السعودية.' },
        { id: 'draft',      title: 'صياغة مستند قانوني',   type: 'أنشئ مسودة وفقاً للأنظمة السعودية من قالب', action: 'صِغ لي مستنداً قانونياً احترافياً متوافقاً مع الأنظمة السعودية.' },
        { id: 'compliance', title: 'فحص الامتثال النظامي', type: 'تحقق من الامتثال للأنظمة السعودية',          action: 'افحص هذا المستند وحدد مدى توافقه مع الأنظمة واللوائح السعودية ذات الصلة.' },
        { id: 'question',   title: 'سؤال قانوني',          type: 'اسأل أي سؤال عن الأنظمة السعودية',           action: 'كم ساعة عمل في الأسبوع وفق نظام العمل السعودي؟' },
      ]
    : [
        { id: 'review',     title: 'Review contract',        type: 'Spot risks in an uploaded contract',           action: 'Upload your contract — I will analyse clauses, flag risks, and surface missing terms under Saudi law.' },
        { id: 'draft',      title: 'Draft a legal document', type: 'Generate a Saudi-compliant draft from a template', action: 'Draft a professional legal document compliant with Saudi regulations.' },
        { id: 'compliance', title: 'Compliance check',       type: 'Verify against Saudi regulatory requirements',  action: 'Audit this document against the relevant Saudi laws and regulations.' },
        { id: 'question',   title: 'Legal question',         type: 'Ask anything about Saudi law',                  action: 'How many work hours per week under Saudi Labor Law?' },
      ];

  const lawChips = isArabic
    ? ['نظام العمل', 'نظام الشركات', 'نظام المعاملات المدنية', 'نظام المحاكم التجارية']
    : ['Labor Law', 'Companies Law', 'Civil Transactions Law', 'Commercial Courts Law'];

  return (
    <div
      className="flex-1 flex flex-col items-center justify-center px-6 py-8 overflow-y-auto"
      data-testid="agent-preview-empty"
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      {/* H.5 — Bayenah logo + time-aware greeting + activity stats.
          Replaces both the old "Welcome to Bayenah, [Name]." modal and
          the "بيّنة" wordmark. */}
      <DashboardHomeHeader isArabic={isArabic} displayName={displayName} />

      {/* H.6 — "Continue where you left off" strip (auto-hides when
          the user has no recent matters). */}
      <RecentMattersStrip isArabic={isArabic} />

      {/* Context links — above input */}
      <div
        className={cn('flex justify-center mb-3', isArabic && 'flex-row-reverse')}
        style={{ gap: '24px' }}
        data-testid="agent-preview-context-links"
      >
        <button
          type="button"
          onClick={composerActions.onVault}
          className="font-arabic"
          style={{
            fontSize: '13px',
            color: '#666666',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
          data-testid="welcome-link-vault"
        >
          <FolderIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
          {isArabic ? 'اختر من الخزانة القانونية' : 'Pick from your Vault'}
        </button>
        <div className="relative">
          <button
            type="button"
            onClick={() => composerActions.onDocType('welcome')}
            className="font-arabic"
            style={{
              fontSize: '13px',
              color: '#666666',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            data-testid="welcome-link-doc-type"
          >
            <FileTextIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
            {isArabic ? 'حدد نوع المستند' : 'Pick document type'}
          </button>
          {docTypeAnchor === 'welcome' && (
            <DocTypePopover
              isArabic={isArabic}
              onPick={(t) => ctx.setDocumentType(t)}
              onClose={closeDocType}
            />
          )}
        </div>
      </div>

      {/* Active context chips (above hero input) */}
      <div style={{ width: '100%', maxWidth: '680px' }}>
        <ContextChipBar ctx={ctx} isArabic={isArabic} />
      </div>

      {/* THE HERO INPUT */}
      <div
        style={{
          width: '100%',
          maxWidth: '680px',
          background: '#F5F5F3',
          border: '1px solid #E0E0E0',
          borderRadius: '12px',
          padding: '16px 16px 12px 16px',
        }}
        data-testid="welcome-input-container"
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={isArabic
            ? 'اسأل بيّنة، صِغ عقداً، أو ارفع مستنداً للمراجعة'
            : 'Ask Bayenah, draft a contract, or upload a document for review'}
          dir={isArabic ? 'rtl' : 'ltr'}
          className="font-arabic"
          style={{
            width: '100%',
            minHeight: '80px',
            border: 'none',
            background: 'transparent',
            resize: 'none',
            fontSize: '14px',
            color: '#111111',
            outline: 'none',
            lineHeight: 1.6,
            fontFamily: 'inherit',
          }}
          data-testid="welcome-textarea"
        />
        {/* Action bar */}
        <div className={cn('flex items-center justify-between mt-2', isArabic && 'flex-row-reverse')}>
          <div className={cn('flex items-center gap-3 relative', isArabic && 'flex-row-reverse')}>
            <button
              type="button"
              onClick={composerActions.onFiles}
              className="font-arabic"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '12px', color: '#666666',
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
              }}
              data-testid="welcome-action-files"
            >
              <PaperclipIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
              {isArabic ? 'ملفات ومصادر' : 'Files & sources'}
            </button>
            <button
              type="button"
              onClick={composerActions.onTemplates}
              className="font-arabic"
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '12px', color: '#666666',
                background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
              }}
              data-testid="welcome-action-templates"
            >
              <ListIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
              {isArabic ? 'نماذج' : 'Templates'}
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={() => composerActions.onCustomize('welcome')}
                className="font-arabic"
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  fontSize: '12px', color: '#666666',
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                }}
                data-testid="welcome-action-customize"
              >
                <SlidersIcon className="w-3.5 h-3.5" strokeWidth={1.7} />
                {isArabic ? 'تخصيص' : 'Customize'}
              </button>
              {customizeAnchor === 'welcome' && (
                <CustomizePopover
                  isArabic={isArabic}
                  settings={ctx.settings}
                  onChange={ctx.updateSetting}
                  onReset={ctx.resetSettings}
                  onClose={closeCustomize}
                />
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!value.trim()}
            className="font-arabic"
            style={{
              background: value.trim() ? '#111111' : '#D4D4D4',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '9999px',
              padding: '7px 16px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: value.trim() ? 'pointer' : 'not-allowed',
              // I.C — Cluster I (Feb 2026): keep the CTA on one line at
              // 375px viewport. "Ask Bayenah" is 11 chars + arrow icon
              // — fits at 13px font / 16px h-padding without wrapping
              // when nowrap is enforced.
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
            data-testid="welcome-submit"
          >
            {isArabic ? 'اسأل بيّنة' : 'Ask Bayenah'}
          </button>
        </div>
      </div>

      {/* Law-source chips — BELOW the input */}
      <div
        className={cn('flex flex-wrap justify-center mt-3', isArabic && 'flex-row-reverse')}
        style={{ gap: '8px', maxWidth: '680px' }}
        data-testid="agent-preview-law-chips"
      >
        {lawChips.map((law) => (
          <span
            key={law}
            className="inline-flex items-center font-arabic"
            style={{
              gap: '6px',
              padding: '5px 12px 5px 8px',
              background: '#FFFFFF',
              border: '1px solid #E0E0E0',
              borderRadius: '9999px',
              fontSize: '12px',
              color: '#444444',
            }}
          >
            <span aria-hidden="true" style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#111111', display: 'inline-block' }} />
            {law}
          </span>
        ))}
      </div>

      {/* Suggested tasks — title + descriptive subtitle */}
      <div style={{ width: '100%', maxWidth: '680px', marginTop: '32px' }}>
        <p
          className="font-arabic"
          style={{
            // AA: #555 on white = 7.5:1 (was #999 ≈ 2.85:1 FAIL)
            fontSize: '11px',
            color: '#555555',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            textAlign: isArabic ? 'right' : 'left',
            marginBottom: '12px',
          }}
        >
          {isArabic ? 'مهام شائعة' : 'Common tasks'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
          {tasks.map((task) => (
            <button
              key={task.id}
              type="button"
              onClick={() => fillTask(task.action)}
              className="font-arabic"
              style={{
                background: '#FFFFFF',
                border: '1px solid #E8E8E8',
                borderRadius: '8px',
                padding: '14px 16px',
                cursor: 'pointer',
                textAlign: isArabic ? 'right' : 'left',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#666666'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E8E8'; }}
              data-testid={`agent-preview-task-${task.id}`}
            >
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#111111' }}>{task.title}</div>
              <div style={{
                // AA: #555 on white = 7.5:1 (was #999 ≈ 2.85:1 FAIL)
                fontSize: '11px', color: '#555555', marginTop: '4px', lineHeight: 1.45,
              }}>{task.type}</div>
            </button>
          ))}
        </div>
      </div>

      {/* H.7 — Today panel (compliance alerts + corpus updates + Herald previews) */}
      <TodayPanel isArabic={isArabic} />

      {/* Disclaimer */}
      <p
        className="font-arabic"
        style={{
          // AA bump: #5e5e5e on white ≈ 6.7:1 (was #BBB ≈ 1.95:1 hard FAIL)
          fontSize: '11px', color: '#5e5e5e', textAlign: 'center', marginTop: '24px',
        }}
      >
        {isArabic ? TERMS.disclaimer : 'For reference only — outputs do not constitute legal advice'}
      </p>
    </div>
  );
}
